import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiActivity,
  FiBarChart2,
  FiCalendar,
  FiDollarSign,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import {
  Area,
  AreaChart,
  Cell,
  CartesianGrid,
  Legend,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { readStoredUser } from "../utils/sessionUser";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { isDemoMode } from "../demo/demoMode";
import { demoReferenceNow, peekDemoSnapshot } from "../demo/demoSnapshot";
import PmsOccupancyDetails from "./PmsOccupancyDetails";

const PAGE_SIZE = 500;
const DAY_MS = 24 * 60 * 60 * 1000;
const FORECAST_MONTH_COUNT = 4; // current month + next 3
const PICKUP_WINDOWS = [
  { key: "m1", label: "1M", days: 30 },
  { key: "w1", label: "1W", days: 7 },
  { key: "d1", label: "1D", days: 1 },
];
const AREA_PALETTE = [
  "#3db6e9",
  "#63c1c7",
  "#6d8ed0",
  "#8ad8e1",
  "#4f79bd",
  "#9ccfed",
  "#80bbb9",
];
const LIGHT_AREA_PALETTE = [
  "#223a7a",
  "#3db6e9",
  "#63c1c7",
  "#4f79bd",
  "#77cdeb",
  "#7bb8bd",
  "#9bb2db",
];
const DONUT_PALETTE = [
  "#3db6e9",
  "#63c1c7",
  "#7896d2",
  "#84d3df",
  "#4f79bd",
  "#a3d9ee",
  "#6baeb4",
  "#b3c3e3",
];
const LIGHT_DONUT_PALETTE = [
  "#223a7a",
  "#3db6e9",
  "#63c1c7",
  "#4f79bd",
  "#77cdeb",
  "#7bb8bd",
  "#9bb2db",
  "#315693",
];
const RADIAN = Math.PI / 180;
const PIE_LABEL_MIN_SHARE = 5;

const PMS_DASHBOARD_MESSAGES = {
  en: {
    title: "PMS Dashboard",
    subtitle: (month) => `Operational pulse and commercial pace. Month focus: ${month}`,
    updated: "Updated:",
    cancelMtd: "Cancel MTD",
    noShowMtd: "No-show MTD",
    losMtd: "LOS MTD",
    refresh: "Refresh",
    exchangeRates: {
      title: "Current exchange rates",
      subtitle: "Used for new quotes and reservations confirmed now.",
      base: "Base currency",
      effective: "Effective",
    },
    states: {
      noPermission: "You do not have permission to view the dashboard.",
      loading: "Loading dashboard data...",
      loadErrorTitle: "Could not load dashboard.",
    },
    messages: {
      reservationsLoadError: "Could not load reservations for dashboard.",
      loadError: "Could not load dashboard.",
    },
    cards: {
      dailyFlow: "Daily Flow",
      dailyFlowHint: (inHouse) => `In-house now: ${inHouse}`,
      pickup: "Pick-up 1M (Room Nights)",
      pickupHint: (delta) => `Occ delta ${delta}`,
      forecast: "Forecast OCC (This Month)",
      forecastHint: (nights, capacity) => `${nights} nights / ${capacity} capacity`,
      revpar: "RevPAR Today",
      revparHint: (adr, occ) => `ADR ${adr} | OCC ${occ}`,
    },
    flow: {
      arrivals: "Arrivals",
      departures: "Departures",
      stayovers: "Stayovers",
      checkedIn: (count) => `Checked in ${count}`,
      checkedOut: (count) => `Checked out ${count}`,
      inHouse: (count) => `In-house ${count}`,
    },
    panels: {
      monthlyCurve: (month) => `Monthly Occupancy Curve - ${month}`,
      monthlyCurveSubtitle: "Stacked areas by room category with total occupancy curve on top.",
      monthlyCurveLegend: (today) =>
        `Reference line marks today (${today}). Areas represent occupied rooms per category; top line is total occupancy.`,
      kpiMatrix: (month) => `KPI Matrix - ${month}`,
      kpiMatrixSubtitle: "Today / Month-to-date / Year-to-date",
      salesByClient: "Sales by Client (MTD)",
      salesByCategory: "Sales by Room Category (MTD)",
      forecast: "Forecast Occupancy + Pick-up (Current Month + Next 3)",
      forecastSubtitle: "Pick-up windows: 1M / 1W / 1D (net room nights + occupancy delta)",
      cityEvents: "Upcoming City Events",
      cityEventsSubtitle: "Demand drivers for pricing and pace",
    },
    table: {
      metric: "Metric",
      today: "Today",
      mtd: "MTD",
      ytd: "YTD",
      bookingsCreated: "Bookings created",
      cancellations: "Cancellations",
      roomNightsSold: "Room nights sold",
      revenue: "Revenue",
      adr: "ADR",
      revpar: "RevPAR",
      occupancy: "Occupancy",
      adults: "Adults",
      children: "Children",
      note:
        "Cancellations show count plus rate over reservations created in the same period. Adults/Children are in-house headcount for today and person-nights for MTD/YTD.",
      month: "Month",
      forecastOcc: "Forecast OCC",
      otbNights: "OTB Nights",
      otbRevenue: "OTB Revenue",
      pickup1m: "Pick-up 1M (RN)",
      pickup1w: "Pick-up 1W (RN)",
      pickup1d: "Pick-up 1D (RN)",
      current: "Current",
      roomNightsShort: "RN",
    },
    chart: {
      totalOccupancy: "Total Occupancy",
      totalOccupancyForecast: "Total Occupancy Forecast",
      totalOccupancyTooltip: (rooms, occ) => `${rooms} rooms (${occ}%)`,
      bookings: "bookings",
    },
    events: {
      empty: "No upcoming events found.",
      special: "Special event",
      attendance: "Attendance",
      to: "to",
      impacts: {
        low: "low",
        medium: "medium",
        high: "high",
        very_high: "very high",
      },
    },
    empty: {
      noDataMonth: "No data this month.",
    },
    fallback: {
      directReservation: "Direct Reservation",
      unmappedCategory: "Unmapped category",
      category: (id) => `Category ${id}`,
      other: (count) => `Other (${count})`,
      otherBase: "Other",
    },
  },
  es: {
    title: "Panel PMS",
    subtitle: (month) => `Pulso operativo y ritmo comercial. Foco del mes: ${month}`,
    updated: "Actualizado:",
    cancelMtd: "Cancel. MTD",
    noShowMtd: "No-show MTD",
    losMtd: "LOS MTD",
    refresh: "Actualizar",
    exchangeRates: {
      title: "Tipos de cambio vigentes",
      subtitle: "Se aplican a nuevas cotizaciones y reservas confirmadas ahora.",
      base: "Moneda base",
      effective: "Vigentes desde",
    },
    states: {
      noPermission: "No tienes permisos para ver el panel.",
      loading: "Cargando datos del panel...",
      loadErrorTitle: "No se ha podido cargar el panel.",
    },
    messages: {
      reservationsLoadError: "No se han podido cargar las reservas para el panel.",
      loadError: "No se ha podido cargar el panel.",
    },
    cards: {
      dailyFlow: "Flujo diario",
      dailyFlowHint: (inHouse) => `En casa ahora: ${inHouse}`,
      pickup: "Pick-up 1M (Room Nights)",
      pickupHint: (delta) => `Delta OCC ${delta}`,
      forecast: "OCC prevista (este mes)",
      forecastHint: (nights, capacity) => `${nights} noches / ${capacity} capacidad`,
      revpar: "RevPAR hoy",
      revparHint: (adr, occ) => `ADR ${adr} | OCC ${occ}`,
    },
    flow: {
      arrivals: "Llegadas",
      departures: "Salidas",
      stayovers: "Alojados",
      checkedIn: (count) => `Check-in ${count}`,
      checkedOut: (count) => `Check-out ${count}`,
      inHouse: (count) => `En casa ${count}`,
    },
    panels: {
      monthlyCurve: (month) => `Curva de ocupaci\u00f3n mensual - ${month}`,
      monthlyCurveSubtitle: "Areas apiladas por categor\u00eda con la curva de ocupaci\u00f3n total por encima.",
      monthlyCurveLegend: (today) =>
        `La l\u00ednea de referencia marca hoy (${today}). Las areas representan habitaciones ocupadas por categor\u00eda; la l\u00ednea superior es la ocupaci\u00f3n total.`,
      kpiMatrix: (month) => `Matriz KPI - ${month}`,
      kpiMatrixSubtitle: "Hoy / Acumulado mes / Acumulado a\u00f1o",
      salesByClient: "Ventas por cliente (MTD)",
      salesByCategory: "Ventas por categor\u00eda de habitaci\u00f3n (MTD)",
      forecast: "Ocupaci\u00f3n prevista + Pick-up (mes actual + siguientes 3)",
      forecastSubtitle: "Ventanas de pick-up: 1M / 1W / 1D (room nights netas + delta de ocupaci\u00f3n)",
      cityEvents: "Pr\u00f3ximos eventos de la ciudad",
      cityEventsSubtitle: "Motores de demanda para pricing y pace",
    },
    table: {
      metric: "M\u00e9trica",
      today: "Hoy",
      mtd: "MTD",
      ytd: "YTD",
      bookingsCreated: "Reservas creadas",
      cancellations: "Cancelaciones",
      roomNightsSold: "Room nights vendidas",
      revenue: "Ingresos",
      adr: "ADR",
      revpar: "RevPAR",
      occupancy: "Ocupaci\u00f3n",
      adults: "Adultos",
      children: "Ni\u00f1os",
      note:
        "Las cancelaciones muestran volumen y ratio sobre las reservas creadas en el mismo periodo. Adultos/Ni\u00f1os muestran personas alojadas hoy y person-nights para MTD/YTD.",
      month: "Mes",
      forecastOcc: "OCC prevista",
      otbNights: "OTB Nights",
      otbRevenue: "Ingresos OTB",
      pickup1m: "Pick-up 1M (RN)",
      pickup1w: "Pick-up 1W (RN)",
      pickup1d: "Pick-up 1D (RN)",
      current: "Actual",
      roomNightsShort: "RN",
    },
    chart: {
      totalOccupancy: "Ocupaci\u00f3n total",
      totalOccupancyForecast: "Ocupaci\u00f3n total prevista",
      totalOccupancyTooltip: (rooms, occ) => `${rooms} habitaciones (${occ}%)`,
      bookings: "reservas",
    },
    events: {
      empty: "No se han encontrado pr\u00f3ximos eventos.",
      special: "Evento especial",
      attendance: "Asistencia",
      to: "a",
      impacts: {
        low: "bajo",
        medium: "medio",
        high: "alto",
        very_high: "muy alto",
      },
    },
    empty: {
      noDataMonth: "Sin datos este mes.",
    },
    fallback: {
      directReservation: "Reserva directa",
      unmappedCategory: "Categor\u00eda sin asignar",
      category: (id) => `Categor\u00eda ${id}`,
      other: (count) => `Otros (${count})`,
      otherBase: "Otros",
    },
  },
  ca: {
    title: "Quadre PMS",
    subtitle: (month) => `Pols operatiu i ritme comercial. Focus del mes: ${month}`,
    updated: "Actualitzat:",
    cancelMtd: "Cancel. MTD",
    noShowMtd: "No-show MTD",
    losMtd: "LOS MTD",
    refresh: "Actualitzar",
    exchangeRates: {
      title: "Tipus de canvi vigents",
      subtitle: "S'apliquen a noves cotitzacions i reserves confirmades ara.",
      base: "Moneda base",
      effective: "Vigents des de",
    },
    states: {
      noPermission: "No tens permisos per veure el quadre.",
      loading: "Carregant dades del quadre...",
      loadErrorTitle: "No s'ha pogut carregar el quadre.",
    },
    messages: {
      reservationsLoadError: "No s'han pogut carregar les reserves per al quadre.",
      loadError: "No s'ha pogut carregar el quadre.",
    },
    cards: {
      dailyFlow: "Flux diari",
      dailyFlowHint: (inHouse) => `A casa ara: ${inHouse}`,
      pickup: "Pick-up 1M (Room Nights)",
      pickupHint: (delta) => `Delta OCC ${delta}`,
      forecast: "OCC prevista (aquest mes)",
      forecastHint: (nights, capacity) => `${nights} nits / ${capacity} capacitat`,
      revpar: "RevPAR avui",
      revparHint: (adr, occ) => `ADR ${adr} | OCC ${occ}`,
    },
    flow: {
      arrivals: "Arribades",
      departures: "Sortides",
      stayovers: "Allotjats",
      checkedIn: (count) => `Check-in ${count}`,
      checkedOut: (count) => `Check-out ${count}`,
      inHouse: (count) => `A casa ${count}`,
    },
    panels: {
      monthlyCurve: (month) => `Corba d'ocupaci\u00f3 mensual - ${month}`,
      monthlyCurveSubtitle: "Areas apilades per categoria amb la corba d'ocupaci\u00f3 total per sobre.",
      monthlyCurveLegend: (today) =>
        `La l\u00ednia de refer\u00e8ncia marca avui (${today}). Les areas representen habitacions ocupades per categoria; la l\u00ednia superior \u00e9s l'ocupaci\u00f3 total.`,
      kpiMatrix: (month) => `Matriu KPI - ${month}`,
      kpiMatrixSubtitle: "Avui / Acumulat mes / Acumulat any",
      salesByClient: "Vendes per client (MTD)",
      salesByCategory: "Vendes per categoria d'habitaci\u00f3 (MTD)",
      forecast: "Ocupaci\u00f3 prevista + Pick-up (mes actual + seg\u00fcents 3)",
      forecastSubtitle: "Finestres de pick-up: 1M / 1W / 1D (room nights netes + delta d'ocupaci\u00f3)",
      cityEvents: "Pr\u00f2xims esdeveniments de la ciutat",
      cityEventsSubtitle: "Motors de demanda per a pricing i pace",
    },
    table: {
      metric: "M\u00e8trica",
      today: "Avui",
      mtd: "MTD",
      ytd: "YTD",
      bookingsCreated: "Reserves creades",
      cancellations: "Cancel.lacions",
      roomNightsSold: "Room nights venudes",
      revenue: "Ingressos",
      adr: "ADR",
      revpar: "RevPAR",
      occupancy: "Ocupaci\u00f3",
      adults: "Adults",
      children: "Nens",
      note:
        "Les cancel.lacions mostren volum i rati sobre les reserves creades en el mateix per\u00edode. Adults/Nens mostren persones allotjades avui i person-nights per a MTD/YTD.",
      month: "Mes",
      forecastOcc: "OCC prevista",
      otbNights: "OTB Nights",
      otbRevenue: "Ingressos OTB",
      pickup1m: "Pick-up 1M (RN)",
      pickup1w: "Pick-up 1W (RN)",
      pickup1d: "Pick-up 1D (RN)",
      current: "Actual",
      roomNightsShort: "RN",
    },
    chart: {
      totalOccupancy: "Ocupaci\u00f3 total",
      totalOccupancyForecast: "Ocupaci\u00f3 total prevista",
      totalOccupancyTooltip: (rooms, occ) => `${rooms} habitacions (${occ}%)`,
      bookings: "reserves",
    },
    events: {
      empty: "No s'han trobat pr\u00f2xims esdeveniments.",
      special: "Esdeveniment especial",
      attendance: "Assist\u00e8ncia",
      to: "a",
      impacts: {
        low: "baix",
        medium: "mitj\u00e0",
        high: "alt",
        very_high: "molt alt",
      },
    },
    empty: {
      noDataMonth: "Sense dades aquest mes.",
    },
    fallback: {
      directReservation: "Reserva directa",
      unmappedCategory: "Categoria sense assignar",
      category: (id) => `Categoria ${id}`,
      other: (count) => `Altres (${count})`,
      otherBase: "Altres",
    },
  },
};

function toIsoDateLocal(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const uiCss = `
  .pd-page { width: 100%; padding: 0 16px 14px; height: calc(100vh - 28px); }
  .pd-shell {
    position: relative;
    height: 100%;
    border-radius: 18px;
    border: 1px solid transparent;
    background:
      linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
      linear-gradient(135deg, rgba(48, 216, 255, 0.55), rgba(37, 154, 211, 0.5), rgba(255, 77, 79, 0.45)) border-box;
    box-shadow: 0 28px 90px rgba(0, 0, 0, 0.56);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .pd-shell::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(900px 300px at 10% 0%, rgba(48, 216, 255, 0.14), transparent 60%),
      radial-gradient(700px 260px at 90% 10%, rgba(37, 154, 211, 0.11), transparent 62%),
      radial-gradient(800px 260px at 40% 110%, rgba(255, 77, 79, 0.1), transparent 60%);
    pointer-events: none;
    opacity: 0.95;
  }
  .pd-shell > * { position: relative; z-index: 1; }
  .pd-header { padding: 16px 16px 14px; border-bottom: 1px solid rgba(255,255,255,0.08); display: grid; grid-template-columns: 1fr auto; gap: 10px 14px; align-items: end; }
  @media (max-width: 980px) {
    .pd-header { grid-template-columns: 1fr; }
  }
  .pd-title {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 900;
    color: transparent;
    background: linear-gradient(90deg, #e6e6f7 0%, rgba(48, 216, 255, 0.96) 42%, rgba(37, 154, 211, 0.92) 92%);
    -webkit-background-clip: text;
    background-clip: text;
    text-shadow: 0 0 24px rgba(48, 216, 255, 0.12);
  }
  .pd-sub { margin-top: 4px; font-size: 0.82rem; color: rgba(230,230,247,0.7); }
  .pd-dateHero {
    margin-top: 8px;
    font-size: 1.62rem;
    font-weight: 900;
    color: rgba(235,246,255,0.98);
    letter-spacing: 0.2px;
    line-height: 1.1;
  }
  .pd-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
  .pd-pill { border: 1px solid rgba(255,255,255,0.16); border-radius: 999px; padding: 6px 10px; font-size: 0.74rem; color: rgba(230,230,247,0.82); background: rgba(255,255,255,0.04); }
  .pd-btn { border: 1px solid rgba(48,216,255,0.35); border-radius: 11px; padding: 8px 11px; font-size: 0.78rem; font-weight: 800; color: #c9f1ff; background: rgba(48,216,255,0.14); cursor: pointer; display: inline-flex; gap: 6px; align-items: center; transition: transform 120ms ease, box-shadow 120ms ease; }
  .pd-btn:hover { transform: translateY(-1px); box-shadow: 0 14px 22px rgba(0,0,0,0.28); }
  .pd-body { padding: 12px; display: flex; flex-direction: column; gap: 10px; overflow: auto; min-height: 0; }
  .pd-state { min-height: 240px; display: flex; flex-direction: column; justify-content: center; align-items: center; color: rgba(230,230,247,0.82); gap: 8px; }
  .pd-spin { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.22); border-top-color: #30d8ff; border-radius: 999px; animation: pd-spin 0.8s linear infinite; }
  @keyframes pd-spin { to { transform: rotate(360deg); } }
  .pd-fxStrip {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 12px;
    border: 1px solid rgba(48,216,255,0.2);
    border-radius: 13px;
    background: linear-gradient(90deg, rgba(48,216,255,0.08), rgba(37,154,211,0.05), rgba(255,255,255,0.025));
  }
  .pd-fxHeading { min-width: 220px; display: flex; align-items: center; gap: 9px; }
  .pd-fxIcon { width: 30px; height: 30px; flex: 0 0 30px; display: grid; place-items: center; border-radius: 10px; color: #bdefff; background: rgba(48,216,255,0.12); }
  .pd-fxTitle { font-size: 0.78rem; font-weight: 900; color: rgba(239,248,255,0.96); }
  .pd-fxSubtitle { margin-top: 2px; font-size: 0.66rem; color: rgba(216,229,244,0.64); }
  .pd-fxRates { min-width: 0; display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }
  .pd-fxChip { display: inline-flex; align-items: center; min-height: 29px; padding: 5px 9px; border: 1px solid rgba(255,255,255,0.11); border-radius: 999px; background: rgba(255,255,255,0.04); color: rgba(230,240,251,0.84); font-size: 0.7rem; white-space: nowrap; }
  .pd-fxChip strong { margin: 0 3px; color: rgba(248,252,255,0.98); font-variant-numeric: tabular-nums; }
  .pd-fxChip--base { border-color: rgba(86,223,169,0.22); background: rgba(86,223,169,0.08); color: rgba(180,244,218,0.94); font-weight: 800; }
  .pd-fxChip--selected { border-color: rgba(48,216,255,0.42); background: rgba(48,216,255,0.14); box-shadow: inset 0 0 0 1px rgba(48,216,255,0.06); }
  .pd-fxEffective { margin-left: auto; color: rgba(216,229,244,0.58); font-size: 0.65rem; white-space: nowrap; }
  .pd-cards { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 8px; }
  .pd-card { border: 1px solid rgba(255,255,255,0.1); border-radius: 13px; background: linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02)); padding: 10px; }
  .pd-cardHead { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
  .pd-cardLabel { font-size: 0.73rem; font-weight: 800; text-transform: uppercase; color: rgba(230,230,247,0.74); }
  .pd-cardValue { margin-top: 7px; font-size: 1.34rem; font-weight: 900; color: rgba(240,245,255,0.96); }
  .pd-cardHint { margin-top: 4px; font-size: 0.74rem; color: rgba(230,230,247,0.62); }
  .pd-cardValueSmall { margin-top: 8px; display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 6px; }
  .pd-miniStat { border: 1px solid rgba(255,255,255,0.11); border-radius: 10px; background: rgba(255,255,255,0.03); padding: 5px 6px; text-align: center; }
  .pd-miniStatLabel { font-size: 0.62rem; color: rgba(230,230,247,0.64); text-transform: uppercase; font-weight: 700; letter-spacing: 0.2px; }
  .pd-miniStatValue { margin-top: 2px; font-size: 0.86rem; font-weight: 800; color: rgba(235,244,255,0.95); }
  .pd-flowGrid { margin-top: 8px; display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 8px; }
  .pd-flowItem {
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px;
    padding: 8px 6px;
    text-align: center;
    background: linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.03));
  }
  .pd-flowLabel { font-size: 0.62rem; font-weight: 800; color: rgba(218,230,245,0.76); text-transform: uppercase; letter-spacing: 0.22px; }
  .pd-flowMain { margin-top: 4px; font-size: 1.2rem; font-weight: 900; color: rgba(240,246,255,0.98); line-height: 1; }
  .pd-flowSub { margin-top: 3px; font-size: 0.66rem; color: rgba(230,230,247,0.66); }
  .pd-grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 8px; }
  .pd-panel { border: 1px solid rgba(255,255,255,0.09); border-radius: 13px; background: rgba(255,255,255,0.03); overflow: hidden; }
  .pd-panel--table {
    border: 1px solid rgba(88, 187, 255, 0.34);
    background:
      linear-gradient(180deg, rgba(20, 35, 58, 0.9), rgba(14, 25, 43, 0.92)) padding-box,
      linear-gradient(135deg, rgba(88, 187, 255, 0.45), rgba(36, 161, 225, 0.35), rgba(106, 221, 255, 0.36)) border-box;
  }
  .pd-panelHeader {
    display: block;
    padding: 11px 12px 10px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    background: rgba(255, 255, 255, 0.03);
  }
  .pd-panel--table .pd-panelHeader {
    background: linear-gradient(180deg, rgba(43, 79, 126, 0.94), rgba(31, 60, 103, 0.92));
    border-bottom: 1px solid rgba(148, 214, 255, 0.28);
  }
  .pd-panelSub { margin-top: 4px; display: block; font-size: 0.7rem; color: rgba(230,230,247,0.7); font-weight: 700; letter-spacing: 0.15px; }
  .pd-panel--table .pd-panelSub { color: rgba(223, 243, 255, 0.86); }
  .pd-panelTitle {
    margin: 0;
    padding: 0;
    font-size: 0.9rem;
    font-weight: 850;
    color: rgba(235,243,255,0.94);
    border-bottom: none;
  }
  .pd-tableWrap { overflow-x: auto; }
  .pd-table { width: 100%; min-width: 560px; border-collapse: separate; border-spacing: 0; table-layout: fixed; }
  .pd-table thead { position: static !important; top: auto !important; z-index: auto !important; background: transparent !important; }
  .pd-table th, .pd-table td { padding: 8px 10px; border-bottom: 1px solid rgba(160,220,255,0.16); color: rgba(230,230,247,0.9); font-size: 0.76rem; }
  .pd-table thead th {
    background: linear-gradient(180deg, rgba(58, 103, 160, 0.95), rgba(40, 79, 130, 0.94)) !important;
    font-size: 0.7rem;
    text-transform: uppercase;
    color: rgba(232,247,255,0.97) !important;
    letter-spacing: 0.24px;
    font-weight: 800;
    border-bottom: 1px solid rgba(168,225,255,0.3) !important;
    box-shadow: inset -1px 0 0 rgba(188, 230, 255, 0.14);
  }
  .pd-table tbody tr { background: rgba(25, 44, 72, 0.8) !important; }
  .pd-table tbody tr:nth-child(even) { background: rgba(34, 58, 92, 0.8) !important; }
  .pd-table tbody tr:hover { background: rgba(45, 74, 114, 0.86) !important; }
  .pd-table tbody td { background: transparent !important; }
  .pd-table td:first-child { font-weight: 800; color: rgba(240,245,255,0.93); }
  .pd-tableMetric { display: inline-flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
  .pd-tableMetricSub { color: rgba(210,234,248,0.74); font-size: 0.68rem; font-weight: 700; }
  .pd-list { padding: 9px 10px 11px; display: flex; flex-direction: column; gap: 8px; }
  .pd-row { display: grid; grid-template-columns: minmax(0,1fr) 70px; gap: 8px; align-items: center; }
  .pd-rowMeta { display: flex; justify-content: space-between; gap: 8px; font-size: 0.74rem; }
  .pd-rowLabel { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 700; }
  .pd-rowValue { color: rgba(230,230,247,0.66); }
  .pd-track { margin-top: 5px; height: 7px; border-radius: 999px; background: rgba(255,255,255,0.08); overflow: hidden; }
  .pd-fill { height: 100%; background: linear-gradient(90deg, rgba(48,216,255,0.92), rgba(56,223,169,0.92)); }
  .pd-share { text-align: right; font-size: 0.76rem; font-weight: 800; color: rgba(230,230,247,0.8); }
  .pd-events { grid-column: 1 / -1; }
  .pd-forecastTable { min-width: 980px; }
  .pd-forecastMonth { display: inline-flex; align-items: center; gap: 6px; font-weight: 800; }
  .pd-currentMonthBadge { border-radius: 999px; padding: 2px 7px; font-size: 0.62rem; font-weight: 800; border: 1px solid rgba(48,216,255,0.30); background: rgba(48,216,255,0.12); color: rgba(196,240,255,0.96); }
  .pd-kicker { font-variant-numeric: tabular-nums; font-weight: 800; }
  .pd-deltaPos { color: rgba(129,245,183,0.96); font-weight: 800; }
  .pd-deltaNeg { color: rgba(255,152,152,0.96); font-weight: 800; }
  .pd-deltaFlat { color: rgba(230,230,247,0.76); font-weight: 800; }
  .pd-matrixNote { padding: 8px 10px 10px; border-top: 1px solid rgba(255,255,255,0.06); font-size: 0.68rem; color: rgba(230,230,247,0.62); }
  .pd-chartWrap { height: 320px; padding: 8px 10px 6px; }
  .pd-chartLegend { padding: 0 11px 11px; font-size: 0.68rem; color: rgba(230,230,247,0.66); }
  .pd-donutPanel { padding: 10px; display: flex; flex-direction: column; gap: 12px; min-height: 240px; }
  .pd-donutChart { height: 240px; min-width: 0; }
  .pd-donutLegend { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 12px; min-width: 0; }
  .pd-donutItem { display: grid; grid-template-columns: 12px minmax(0, 1fr); gap: 8px; align-items: center; }
  .pd-donutSwatch { width: 10px; height: 10px; border-radius: 999px; box-shadow: 0 0 12px rgba(255,255,255,0.16); }
  .pd-donutLabel { min-width: 0; }
  .pd-donutName { font-size: 0.74rem; font-weight: 800; color: rgba(240,245,255,0.94); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pd-donutMeta { margin-top: 2px; font-size: 0.68rem; color: rgba(210,230,245,0.68); }
  .pd-donutEmpty { padding: 14px 12px 18px; font-size: 0.74rem; color: rgba(230,230,247,0.62); }
  .pd-event { border: 1px solid rgba(255,255,255,0.08); border-radius: 11px; background: rgba(255,255,255,0.02); padding: 8px 9px; display: flex; justify-content: space-between; gap: 8px; align-items: center; }
  .pd-eventName { font-size: 0.76rem; font-weight: 750; color: rgba(230,230,247,0.88); }
  .pd-eventMeta { margin-top: 2px; font-size: 0.69rem; color: rgba(230,230,247,0.63); }
  .pd-impact { border-radius: 999px; border: 1px solid rgba(255,255,255,0.16); padding: 4px 8px; font-size: 0.67rem; font-weight: 800; color: rgba(230,230,247,0.88); white-space: nowrap; }
  .pd-impact-low { background: rgba(96,125,139,0.2); }
  .pd-impact-medium { background: rgba(41,98,255,0.22); }
  .pd-impact-high { background: rgba(230,81,0,0.23); }
  .pd-impact-very_high { background: rgba(183,28,28,0.28); }
  .pd-page--light { color: #214150; }
  .pd-page--light .pd-shell {
    border-color: rgba(165, 126, 78, 0.18);
    background:
      linear-gradient(180deg, rgba(250, 245, 238, 0.98), rgba(241, 234, 224, 0.98)) padding-box,
      linear-gradient(135deg, rgba(83, 121, 134, 0.22), rgba(165, 126, 78, 0.24), rgba(205, 162, 114, 0.2)) border-box;
    box-shadow: 0 28px 80px rgba(95, 72, 40, 0.14);
  }
  .pd-page--light .pd-shell::before {
    background:
      radial-gradient(980px 340px at 6% -6%, rgba(83, 121, 134, 0.14), transparent 62%),
      radial-gradient(840px 320px at 100% 0%, rgba(170, 124, 69, 0.16), transparent 60%),
      radial-gradient(880px 320px at 44% 112%, rgba(201, 145, 85, 0.12), transparent 62%);
    opacity: 1;
  }
  .pd-page--light .pd-header { border-bottom-color: rgba(24, 54, 69, 0.1); }
  .pd-page--light .pd-title {
    background: linear-gradient(90deg, #1d3b49 0%, #587482 54%, #a36f39 100%);
    -webkit-background-clip: text;
    background-clip: text;
    text-shadow: none;
  }
  .pd-page--light .pd-sub { color: #5c6f7b; }
  .pd-page--light .pd-dateHero { color: #173544; }
  .pd-page--light .pd-pill {
    border-color: rgba(133, 106, 73, 0.16);
    color: #49626e;
    background: rgba(255, 250, 244, 0.82);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }
  .pd-page--light .pd-btn {
    border-color: rgba(164, 115, 63, 0.34);
    color: #fffaf1;
    background: linear-gradient(135deg, #8d663d 0%, #b68755 60%, #c89c68 100%);
    box-shadow: 0 14px 28px rgba(101, 78, 45, 0.16);
  }
  .pd-page--light .pd-btn:hover { box-shadow: 0 18px 34px rgba(101, 78, 45, 0.22); }
  .pd-page--light .pd-state { color: #214150; }
  .pd-page--light .pd-spin {
    border-color: rgba(24, 54, 69, 0.18);
    border-top-color: #8a5d2e;
  }
  .pd-page--light .pd-card {
    border-color: rgba(165, 126, 78, 0.14);
    background: linear-gradient(180deg, rgba(255, 252, 247, 0.94), rgba(246, 240, 232, 0.92));
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72), 0 16px 28px rgba(100, 78, 47, 0.08);
  }
  .pd-page--light .pd-cardLabel { color: #7b6958; }
  .pd-page--light .pd-cardValue { color: #183746; }
  .pd-page--light .pd-cardHint { color: #6a7b85; }
  .pd-page--light .pd-fxStrip { border-color: rgba(83,121,134,0.16); background: linear-gradient(90deg, rgba(83,121,134,0.09), rgba(180,136,83,0.055), rgba(255,255,255,0.38)); }
  .pd-page--light .pd-fxIcon { color: #436b7b; background: rgba(83,121,134,0.1); }
  .pd-page--light .pd-fxTitle { color: #183746; }
  .pd-page--light .pd-fxSubtitle,.pd-page--light .pd-fxEffective { color: #6a7b85; }
  .pd-page--light .pd-fxChip { border-color: rgba(24,54,69,0.1); background: rgba(255,252,247,0.74); color: #526a77; }
  .pd-page--light .pd-fxChip strong { color: #183746; }
  .pd-page--light .pd-fxChip--base { border-color: rgba(47,123,87,0.2); background: rgba(47,123,87,0.08); color: #2f7355; }
  .pd-page--light .pd-fxChip--selected { border-color: rgba(83,121,134,0.34); background: rgba(83,121,134,0.12); }
  .pd-page--light .pd-miniStat {
    border-color: rgba(133, 106, 73, 0.12);
    background: rgba(255, 252, 247, 0.84);
  }
  .pd-page--light .pd-miniStatLabel { color: #7c6a58; }
  .pd-page--light .pd-miniStatValue { color: #183746; }
  .pd-page--light .pd-flowItem {
    border-color: rgba(133, 106, 73, 0.12);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.82), rgba(245, 238, 229, 0.86));
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
  }
  .pd-page--light .pd-flowLabel { color: #7c6a58; }
  .pd-page--light .pd-flowMain { color: #173544; }
  .pd-page--light .pd-flowSub { color: #667884; }
  .pd-page--light .pd-panel {
    border-color: rgba(24, 54, 69, 0.1);
    background: linear-gradient(180deg, rgba(255, 252, 247, 0.84), rgba(247, 241, 233, 0.78));
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
  }
  .pd-page--light .pd-panel--table {
    border-color: rgba(165, 126, 78, 0.18);
    background:
      linear-gradient(180deg, rgba(255, 252, 247, 0.92), rgba(244, 237, 228, 0.94)) padding-box,
      linear-gradient(135deg, rgba(73, 116, 132, 0.2), rgba(165, 126, 78, 0.2), rgba(205, 162, 114, 0.18)) border-box;
  }
  .pd-page--light .pd-panelHeader {
    border-bottom-color: rgba(24, 54, 69, 0.08);
    background: rgba(255, 255, 255, 0.34);
  }
  .pd-page--light .pd-panel--table .pd-panelHeader {
    background: linear-gradient(180deg, rgba(247, 241, 233, 0.98), rgba(239, 231, 219, 0.99));
    border-bottom-color: rgba(122, 95, 58, 0.12);
  }
  .pd-page--light .pd-panelTitle { color: #1d3b49; }
  .pd-page--light .pd-panelSub { color: #6c7d87; }
  .pd-page--light .pd-panel--table .pd-panelSub { color: #6a7b86; }
  .pd-page--light .pd-tableWrap {
    scrollbar-color: rgba(165, 126, 78, 0.5) rgba(208, 197, 183, 0.45);
  }
  .pd-page--light .pd-tableWrap::-webkit-scrollbar { width: 8px; height: 8px; }
  .pd-page--light .pd-tableWrap::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(165, 126, 78, 0.85), rgba(100, 129, 142, 0.85));
    border-radius: 999px;
  }
  .pd-page--light .pd-tableWrap::-webkit-scrollbar-track {
    background: rgba(208, 197, 183, 0.38);
    border-radius: 999px;
  }
  .pd-page--light .pd-table th,
  .pd-page--light .pd-table td {
    border-bottom-color: rgba(133, 106, 73, 0.12);
    color: #244453;
  }
  .pd-page--light .pd-table thead th {
    background: linear-gradient(180deg, rgba(243, 236, 226, 0.98), rgba(235, 226, 213, 0.98)) !important;
    color: #24414d !important;
    border-bottom-color: rgba(122, 95, 58, 0.14) !important;
    box-shadow: inset -1px 0 0 rgba(122, 95, 58, 0.08);
  }
  .pd-page--light .pd-table tbody tr { background: rgba(255, 252, 247, 0.72) !important; }
  .pd-page--light .pd-table tbody tr:nth-child(even) { background: rgba(246, 240, 232, 0.82) !important; }
  .pd-page--light .pd-table tbody tr:hover { background: rgba(236, 229, 219, 0.92) !important; }
  .pd-page--light .pd-table td:first-child { color: #183746; }
  .pd-page--light .pd-tableMetricSub { color: #6c7d87; }
  .pd-page--light .pd-rowValue { color: #6b7d87; }
  .pd-page--light .pd-track {
    background: rgba(24, 54, 69, 0.08);
    box-shadow: inset 0 0 0 1px rgba(24, 54, 69, 0.05);
  }
  .pd-page--light .pd-fill { background: linear-gradient(90deg, #5f7f8f, #b68755); }
  .pd-page--light .pd-share { color: #516673; }
  .pd-page--light .pd-currentMonthBadge {
    border-color: rgba(164, 115, 63, 0.24);
    background: rgba(191, 149, 100, 0.16);
    color: #7b572f;
  }
  .pd-page--light .pd-kicker { color: #173544; }
  .pd-page--light .pd-deltaPos { color: #2f7b57; }
  .pd-page--light .pd-deltaNeg { color: #9b4d4d; }
  .pd-page--light .pd-deltaFlat { color: #6b7d87; }
  .pd-page--light .pd-matrixNote {
    border-top-color: rgba(24, 54, 69, 0.08);
    color: #687b86;
  }
  .pd-page--light .pd-chartLegend { color: #6b7d87; }
  .pd-page--light .pd-donutSwatch { box-shadow: 0 0 12px rgba(108, 87, 54, 0.18); }
  .pd-page--light .pd-donutName { color: #1d3b49; }
  .pd-page--light .pd-donutMeta { color: #6c7d87; }
  .pd-page--light .pd-donutEmpty { color: #6c7d87; }
  .pd-page--light .pd-event {
    border-color: rgba(133, 106, 73, 0.12);
    background: linear-gradient(180deg, rgba(255, 252, 247, 0.86), rgba(245, 238, 229, 0.88));
    box-shadow: 0 14px 24px rgba(100, 78, 47, 0.06);
  }
  .pd-page--light .pd-eventName { color: #1d3b49; }
  .pd-page--light .pd-eventMeta { color: #6c7d87; }
  .pd-page--light .pd-impact {
    border-color: rgba(24, 54, 69, 0.12);
    color: #214150;
    background: rgba(24, 54, 69, 0.05);
  }
  .pd-page--light .pd-impact-low { background: rgba(110, 126, 137, 0.14); }
  .pd-page--light .pd-impact-medium { background: rgba(73, 116, 132, 0.12); }
  .pd-page--light .pd-impact-high {
    background: rgba(187, 122, 89, 0.2);
    color: #fffaf1;
    border-color: rgba(165, 126, 78, 0.22);
  }
  .pd-page--light .pd-impact-very_high {
    background: rgba(154, 78, 68, 0.26);
    color: #fffaf1;
    border-color: rgba(154, 78, 68, 0.24);
  }

  /* Pilcore 2026 brand layer */
  .pd-page {
    --pilcore-blue: #223a7a;
    --pilcore-horizon: #3db6e9;
    --pilcore-connect: #63c1c7;
    --pilcore-ink: #172653;
    font-family: "Montserrat", sans-serif;
    color: rgba(255, 255, 255, 0.94);
  }

  .pd-shell {
    border-color: rgba(61, 182, 233, 0.42);
    background: linear-gradient(155deg, #172653 0%, #142349 54%, #0f1934 100%);
    box-shadow: 0 24px 64px rgba(7, 17, 45, 0.34);
  }

  .pd-shell::before {
    background:
      radial-gradient(860px 300px at 6% -8%, rgba(61, 182, 233, 0.17), transparent 62%),
      radial-gradient(720px 300px at 100% 2%, rgba(99, 193, 199, 0.12), transparent 64%);
    opacity: 1;
  }

  .pd-header {
    border-bottom-color: rgba(61, 182, 233, 0.2);
    background: rgba(23, 38, 83, 0.24);
  }

  .pd-title {
    color: #ffffff;
    background: none;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-shadow: none;
    -webkit-text-fill-color: currentColor;
  }

  .pd-sub {
    color: rgba(255, 255, 255, 0.65);
    font-weight: 300;
  }

  .pd-dateHero {
    color: var(--pilcore-horizon);
    font-weight: 600;
  }

  .pd-pill {
    border-color: rgba(61, 182, 233, 0.26);
    color: rgba(255, 255, 255, 0.78);
    background: rgba(255, 255, 255, 0.055);
  }

  .pd-btn {
    border-color: var(--pilcore-horizon);
    color: var(--pilcore-blue);
    background: var(--pilcore-horizon);
    font-weight: 700;
    box-shadow: 0 8px 18px rgba(61, 182, 233, 0.18);
  }

  .pd-btn:hover {
    box-shadow: 0 12px 24px rgba(61, 182, 233, 0.28);
  }

  .pd-spin {
    border-color: rgba(255, 255, 255, 0.2);
    border-top-color: var(--pilcore-horizon);
  }

  .pd-fxStrip {
    border-color: rgba(99, 193, 199, 0.3);
    background: linear-gradient(90deg, rgba(61, 182, 233, 0.1), rgba(99, 193, 199, 0.07));
  }

  .pd-fxIcon,
  .pd-cardHead > svg,
  .pd-panelTitle > svg {
    box-sizing: content-box;
    border: 1px solid rgba(61, 182, 233, 0.7);
    border-radius: 10px;
    padding: 6px;
    color: var(--pilcore-horizon) !important;
    background: var(--pilcore-blue);
  }

  .pd-fxTitle,
  .pd-cardValue,
  .pd-flowMain,
  .pd-panelTitle,
  .pd-donutName,
  .pd-eventName {
    color: rgba(255, 255, 255, 0.95);
  }

  .pd-fxSubtitle,
  .pd-fxEffective,
  .pd-cardHint,
  .pd-flowSub,
  .pd-panelSub,
  .pd-chartLegend,
  .pd-donutMeta,
  .pd-eventMeta {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 300;
  }

  .pd-fxChip {
    border-color: rgba(61, 182, 233, 0.22);
    background: rgba(255, 255, 255, 0.05);
  }

  .pd-fxChip--base,
  .pd-fxChip--selected {
    border-color: rgba(99, 193, 199, 0.5);
    background: rgba(99, 193, 199, 0.14);
    color: #d9fbfb;
  }

  .pd-card,
  .pd-flowItem,
  .pd-miniStat,
  .pd-panel,
  .pd-event {
    border-color: rgba(61, 182, 233, 0.16);
    background: rgba(255, 255, 255, 0.045);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
  }

  .pd-card,
  .pd-panel {
    border-radius: 16px;
  }

  .pd-cardLabel,
  .pd-flowLabel,
  .pd-miniStatLabel {
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
    letter-spacing: 0.065em;
  }

  .pd-cardValue,
  .pd-flowMain,
  .pd-miniStatValue {
    font-weight: 600;
  }

  .pd-panel--table {
    border-color: rgba(61, 182, 233, 0.34);
    background: rgba(23, 38, 83, 0.48);
  }

  .pd-panelHeader,
  .pd-panel--table .pd-panelHeader {
    border-bottom-color: rgba(61, 182, 233, 0.18);
    background: rgba(34, 58, 122, 0.32);
  }

  .pd-panelTitle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
  }

  .pd-panelTitle > svg {
    margin-right: 0 !important;
    flex: 0 0 auto;
  }

  .pd-table th,
  .pd-table td {
    border-bottom-color: rgba(61, 182, 233, 0.14);
  }

  .pd-table thead th {
    background: rgba(34, 58, 122, 0.96) !important;
    border-bottom-color: rgba(61, 182, 233, 0.34) !important;
    color: #ffffff !important;
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  .pd-table tbody tr,
  .pd-table tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.025) !important;
  }

  .pd-table tbody tr:nth-child(even) {
    background: rgba(61, 182, 233, 0.035) !important;
  }

  .pd-table tbody tr:hover {
    background: rgba(61, 182, 233, 0.09) !important;
  }

  .pd-track {
    background: rgba(255, 255, 255, 0.08);
  }

  .pd-fill {
    background: linear-gradient(90deg, var(--pilcore-horizon), var(--pilcore-connect));
  }

  .pd-currentMonthBadge {
    border-color: rgba(61, 182, 233, 0.45);
    background: rgba(61, 182, 233, 0.14);
    color: #cdefff;
  }

  .pd-page--light {
    color: var(--pilcore-ink);
  }

  .pd-page--light .pd-shell {
    border-color: rgba(34, 58, 122, 0.14);
    background: linear-gradient(180deg, #ffffff 0%, #f8fbfd 100%);
    box-shadow: 0 22px 54px rgba(34, 58, 122, 0.12);
  }

  .pd-page--light .pd-shell::before {
    background:
      radial-gradient(920px 320px at 4% -8%, rgba(61, 182, 233, 0.12), transparent 64%),
      radial-gradient(760px 300px at 100% 0%, rgba(99, 193, 199, 0.08), transparent 66%);
  }

  .pd-page--light .pd-header {
    border-bottom-color: rgba(34, 58, 122, 0.1);
    background: rgba(255, 255, 255, 0.68);
  }

  .pd-page--light .pd-title,
  .pd-page--light .pd-dateHero,
  .pd-page--light .pd-cardValue,
  .pd-page--light .pd-flowMain,
  .pd-page--light .pd-miniStatValue,
  .pd-page--light .pd-panelTitle,
  .pd-page--light .pd-donutName,
  .pd-page--light .pd-eventName,
  .pd-page--light .pd-kicker,
  .pd-page--light .pd-table td:first-child {
    color: var(--pilcore-ink);
    background: none;
    -webkit-text-fill-color: currentColor;
  }

  .pd-page--light .pd-dateHero {
    color: var(--pilcore-blue);
  }

  .pd-page--light .pd-sub,
  .pd-page--light .pd-cardHint,
  .pd-page--light .pd-flowSub,
  .pd-page--light .pd-panelSub,
  .pd-page--light .pd-chartLegend,
  .pd-page--light .pd-donutMeta,
  .pd-page--light .pd-eventMeta,
  .pd-page--light .pd-fxSubtitle,
  .pd-page--light .pd-fxEffective,
  .pd-page--light .pd-tableMetricSub,
  .pd-page--light .pd-matrixNote,
  .pd-page--light .pd-donutEmpty {
    color: rgba(23, 38, 83, 0.62);
  }

  .pd-page--light .pd-pill,
  .pd-page--light .pd-fxChip {
    border-color: rgba(34, 58, 122, 0.12);
    color: rgba(23, 38, 83, 0.72);
    background: #ffffff;
    box-shadow: none;
  }

  .pd-page--light .pd-btn {
    border-color: var(--pilcore-horizon);
    color: var(--pilcore-blue);
    background: var(--pilcore-horizon);
    box-shadow: 0 8px 18px rgba(61, 182, 233, 0.2);
  }

  .pd-page--light .pd-state {
    color: var(--pilcore-ink);
  }

  .pd-page--light .pd-fxStrip {
    border-color: rgba(99, 193, 199, 0.34);
    background: linear-gradient(90deg, rgba(61, 182, 233, 0.08), rgba(99, 193, 199, 0.06));
  }

  .pd-page--light .pd-fxTitle {
    color: var(--pilcore-ink);
  }

  .pd-page--light .pd-fxChip strong {
    color: var(--pilcore-blue);
  }

  .pd-page--light .pd-fxChip--base,
  .pd-page--light .pd-fxChip--selected {
    border-color: rgba(99, 193, 199, 0.5);
    color: var(--pilcore-blue);
    background: rgba(99, 193, 199, 0.12);
  }

  .pd-page--light .pd-card,
  .pd-page--light .pd-flowItem,
  .pd-page--light .pd-miniStat,
  .pd-page--light .pd-panel,
  .pd-page--light .pd-event {
    border-color: rgba(34, 58, 122, 0.11);
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(34, 58, 122, 0.055);
  }

  .pd-page--light .pd-cardLabel,
  .pd-page--light .pd-flowLabel,
  .pd-page--light .pd-miniStatLabel {
    color: rgba(34, 58, 122, 0.68);
  }

  .pd-page--light .pd-panel--table {
    border-color: rgba(61, 182, 233, 0.32);
    background: #ffffff;
  }

  .pd-page--light .pd-panelHeader,
  .pd-page--light .pd-panel--table .pd-panelHeader {
    border-bottom-color: rgba(34, 58, 122, 0.1);
    background: #f5f9fc;
  }

  .pd-page--light .pd-table th,
  .pd-page--light .pd-table td {
    border-bottom-color: rgba(34, 58, 122, 0.1);
    color: rgba(23, 38, 83, 0.84);
  }

  .pd-page--light .pd-table thead th {
    background: var(--pilcore-blue) !important;
    border-bottom-color: rgba(61, 182, 233, 0.5) !important;
    color: #ffffff !important;
    box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.1);
  }

  .pd-page--light .pd-table tbody tr {
    background: #ffffff !important;
  }

  .pd-page--light .pd-table tbody tr:nth-child(even) {
    background: #f5f9fc !important;
  }

  .pd-page--light .pd-table tbody tr:hover {
    background: rgba(61, 182, 233, 0.1) !important;
  }

  .pd-page--light .pd-track {
    background: rgba(34, 58, 122, 0.1);
  }

  .pd-page--light .pd-fill {
    background: linear-gradient(90deg, var(--pilcore-horizon), var(--pilcore-connect));
  }

  .pd-page--light .pd-currentMonthBadge {
    border-color: rgba(61, 182, 233, 0.42);
    background: rgba(61, 182, 233, 0.12);
    color: var(--pilcore-blue);
  }

  .pd-page--light .pd-deltaPos { color: #2b7f66; }
  .pd-page--light .pd-deltaNeg { color: #a44a57; }
  .pd-page--light .pd-deltaFlat { color: rgba(23, 38, 83, 0.62); }

  .pd-page--light .pd-impact {
    border-color: rgba(34, 58, 122, 0.14);
    color: var(--pilcore-ink);
    background: rgba(34, 58, 122, 0.05);
  }

  .pd-page--light .pd-impact-medium {
    background: rgba(61, 182, 233, 0.14);
  }

  .pd-page--light .pd-impact-high,
  .pd-page--light .pd-impact-very_high {
    color: #ffffff;
  }
  @media (max-width: 1400px) {
    .pd-cards { grid-template-columns: repeat(2, minmax(0,1fr)); }
  }
  @media (max-width: 1180px) {
    .pd-grid { grid-template-columns: 1fr; }
  }
  @media (max-width: 860px) {
    .pd-cards { grid-template-columns: repeat(2, minmax(0,1fr)); }
    .pd-fxStrip { align-items: flex-start; flex-direction: column; }
    .pd-fxEffective { margin-left: 0; }
  }
  @media (max-width: 700px) {
    .pd-page { padding: 0 10px 10px; height: calc(100vh - 20px); }
  }
  @media (max-width: 560px) {
    .pd-cards { grid-template-columns: 1fr; }
    .pd-flowGrid { grid-template-columns: 1fr; }
    .pd-donutChart { height: 200px; }
    .pd-donutLegend { grid-template-columns: 1fr; }
  }
  @media (max-height: 820px) {
    .pd-shell { zoom: 0.9; height: calc((100dvh - 28px) / 0.9); }
  }
  @media (max-height: 720px) {
    .pd-shell { zoom: 0.8; height: calc((100dvh - 28px) / 0.8); }
  }
  @media (max-height: 620px) {
    .pd-shell { zoom: 0.7; height: calc((100dvh - 28px) / 0.7); }
  }
`;

function normalizeDate(value) {
  return value ? String(value).slice(0, 10) : "";
}

function parseDateOnly(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function addDays(iso, days) {
  const dt = parseDateOnly(iso);
  if (!dt) return iso;
  dt.setDate(dt.getDate() + days);
  return toIsoDateLocal(dt);
}

function startOfMonthIso(date) {
  return toIsoDateLocal(new Date(date.getFullYear(), date.getMonth(), 1));
}

function endOfMonthIso(date) {
  return toIsoDateLocal(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function nightsDiff(checkIn, checkOut) {
  const a = parseDateOnly(normalizeDate(checkIn));
  const b = parseDateOnly(normalizeDate(checkOut));
  if (!a || !b) return 0;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / DAY_MS));
}

function overlapNights(checkIn, checkOut, fromIso, toIso) {
  const stayStart = parseDateOnly(normalizeDate(checkIn));
  const stayEnd = parseDateOnly(normalizeDate(checkOut));
  const rangeStart = parseDateOnly(fromIso);
  const rangeEnd = parseDateOnly(addDays(toIso, 1));
  if (!stayStart || !stayEnd || !rangeStart || !rangeEnd || stayEnd <= stayStart) return 0;
  const overlapStart = Math.max(stayStart.getTime(), rangeStart.getTime());
  const overlapEnd = Math.min(stayEnd.getTime(), rangeEnd.getTime());
  if (overlapEnd <= overlapStart) return 0;
  return Math.round((overlapEnd - overlapStart) / DAY_MS);
}

function isCancelled(status) {
  const s = String(status || "").toLowerCase();
  return s === "cancelled" || s === "canceled";
}

function cancellationDate(reservation) {
  return normalizeDate(
    reservation?.cancelled_at ||
      reservation?.cancelledAt ||
      reservation?.canceled_at ||
      reservation?.canceledAt ||
      reservation?.updatedAt ||
      reservation?.updated_at
  );
}

function isNoShow(status) {
  return String(status || "").toLowerCase() === "no show";
}

function isOperational(status) {
  const s = String(status || "").toLowerCase();
  if (!s) return true;
  if (s === "draft" || s === "requested") return false;
  return !isCancelled(s) && !isNoShow(s);
}

function toRevenue(reservation) {
  const base = Number(reservation?.total_price || 0);
  const extras = Number(reservation?.reservation_extras_total ?? reservation?.extras_total ?? 0);
  return (Number.isFinite(base) ? base : 0) + (Number.isFinite(extras) ? extras : 0);
}

function reservationNights(reservation) {
  const n = Number(reservation?.room_nights || 0);
  if (Number.isFinite(n) && n > 0) return Math.round(n);
  return nightsDiff(reservation?.check_in_date, reservation?.check_out_date);
}

function rowsFromPayload(payload) {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.rows) ? payload.rows : [];
}

async function fetchAllReservations(businessUnitId, signal) {
  const first = await axios.get(`/api/reservations?business_unit_id=${businessUnitId}&page=1&page_size=${PAGE_SIZE}&show_cancelled=1`, { signal });
  const firstRows = rowsFromPayload(first?.data);
  const totalPages = Array.isArray(first?.data) ? 1 : Math.max(1, Number(first?.data?.total_pages || 1));
  if (totalPages <= 1) return firstRows;
  const all = [...firstRows];
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await axios.get(`/api/reservations?business_unit_id=${businessUnitId}&page=${page}&page_size=${PAGE_SIZE}&show_cancelled=1`, { signal });
    all.push(...rowsFromPayload(next?.data));
  }
  return all;
}

function yearDayIndex(date) {
  const start = new Date(date.getFullYear(), 0, 1);
  return Math.floor((date.getTime() - start.getTime()) / DAY_MS) + 1;
}

function monthLabel(date, format = "long", locale = "en-US") {
  return date.toLocaleDateString(locale, {
    month: format,
    year: "numeric",
  });
}

function ordinalSuffix(day) {
  const mod10 = day % 10;
  const mod100 = day % 100;
  if (mod10 === 1 && mod100 !== 11) return "st";
  if (mod10 === 2 && mod100 !== 12) return "nd";
  if (mod10 === 3 && mod100 !== 13) return "rd";
  return "th";
}

function formatHeroDate(date, languageId = "en", locale = "en-US") {
  if (languageId !== "en") {
    return date.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  const day = date.getDate();
  const month = date.toLocaleDateString(locale, { month: "long" });
  const year = date.getFullYear();
  return `${day}${ordinalSuffix(day)} of ${month} ${year}`;
}

function formatExchangeRate(value, locale = "en-US") {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 12,
  }).format(numeric);
}

function isWithinWindow(dateIso, todayIso, days) {
  if (!dateIso) return false;
  const cutoff = addDays(todayIso, -days);
  return dateIso >= cutoff && dateIso <= todayIso;
}

function toNum(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function impactClass(impact) {
  const key = String(impact || "medium").toLowerCase();
  if (key === "very_high") return "pd-impact-very_high";
  if (key === "high") return "pd-impact-high";
  if (key === "low") return "pd-impact-low";
  return "pd-impact-medium";
}

function formatImpactLabel(impact, copy = PMS_DASHBOARD_MESSAGES.en) {
  const key = String(impact || "medium").toLowerCase();
  return copy?.events?.impacts?.[key] || key.replace("_", " ");
}

function PmsDashboard() {
  const {
    formatFromBase,
    enabled: multiCurrencyEnabled,
    baseCurrency,
    displayCurrency,
    currencies,
    rates: currencyRates,
    configuration: currencyConfiguration,
  } = useCurrencyDisplay();
  const { themeId, languageId, language } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const dashboardCopy = PMS_DASHBOARD_MESSAGES[languageId] || PMS_DASHBOARD_MESSAGES.en;
  const textLocale = languageId === "en" ? "en-US" : language?.locale || "es-ES";
  const sortLocale = languageId === "en" ? undefined : language?.locale || undefined;
  const areaPalette = isLightTheme ? LIGHT_AREA_PALETTE : AREA_PALETTE;
  const donutPalette = isLightTheme ? LIGHT_DONUT_PALETTE : DONUT_PALETTE;
  const messageRef = useRef(dashboardCopy.messages);
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "1";
  const currentUser = readStoredUser();
  const isAdminRole = Number(currentUser?.id) === 1;
  const canViewDashboard = isAdminRole || Boolean(currentUser?.permissions?.["reservations.view_dashboard"]);

  const exchangeRateRows = useMemo(
    () => (Array.isArray(currencies) ? currencies : [])
      .map((code) => String(code || "").trim().toUpperCase())
      .filter((code) => code && code !== baseCurrency)
      .map((code) => ({ code, rate: currencyRates?.[code] }))
      .filter((row) => Number(row.rate) > 0),
    [baseCurrency, currencies, currencyRates]
  );

  useEffect(() => {
    messageRef.current = dashboardCopy.messages;
  }, [dashboardCopy.messages]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomCategories, setRoomCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [occupancySelection, setOccupancySelection] = useState(null);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      setError("");

      const [resTask, roomsTask, categoriesTask, clientsTask, eventsTask] = await Promise.allSettled([
        fetchAllReservations(businessUnitId, controller.signal),
        axios.get("/api/rooms", { params: { business_unit_id: businessUnitId }, signal: controller.signal }),
        axios.get("/api/room_categories", {
          params: { business_unit_id: businessUnitId },
          signal: controller.signal,
        }),
        axios.get(`/api/client-category-allocation/clients?business_unit_id=${businessUnitId}`, {
          signal: controller.signal,
        }),
        axios.get("/api/city-events", { params: { business_unit_id: businessUnitId }, signal: controller.signal }),
      ]);

      if (!active) return;

      if (resTask.status === "fulfilled") {
        setReservations(Array.isArray(resTask.value) ? resTask.value : []);
      } else {
        setReservations([]);
        setError(resTask?.reason?.response?.data?.error || messageRef.current.reservationsLoadError);
      }

      setRooms(roomsTask.status === "fulfilled" && Array.isArray(roomsTask.value?.data) ? roomsTask.value.data : []);
      setRoomCategories(
        categoriesTask.status === "fulfilled" && Array.isArray(categoriesTask.value?.data)
          ? categoriesTask.value.data
          : []
      );
      setClients(
        clientsTask.status === "fulfilled" && Array.isArray(clientsTask.value?.data)
          ? clientsTask.value.data
          : []
      );
      setEvents(eventsTask.status === "fulfilled" && Array.isArray(eventsTask.value?.data) ? eventsTask.value.data : []);

      setUpdatedAt(isDemoMode() ? peekDemoSnapshot()?.generated_at || "" : new Date().toISOString());
      setLoading(false);
    };

    run().catch((err) => {
      if (!active) return;
      setError(err?.response?.data?.error || messageRef.current.loadError);
      setLoading(false);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [businessUnitId, reloadKey, crossTabRefreshToken]);

  const intFmt = useMemo(() => new Intl.NumberFormat("es-ES"), []);

  const clientMap = useMemo(() => {
    const map = {};
    (Array.isArray(clients) ? clients : []).forEach((client) => {
      const id = client?.id;
      const name = String(client?.name || client?.agency_name || "").trim();
      if (id == null || !name) return;
      map[String(id)] = name;
    });
    return map;
  }, [clients]);

  const categoryMap = useMemo(() => {
    const map = {};

    (Array.isArray(roomCategories) ? roomCategories : []).forEach((category) => {
      const id = category?.id;
      const name = String(category?.name || "").trim();
      if (id == null || !name) return;
      map[String(id)] = name;
    });

    (Array.isArray(rooms) ? rooms : []).forEach((room) => {
      const id = room?.room_category_id ?? room?.room_category?.id ?? room?.RoomCategory?.id;
      const name = String(
        room?.RoomCategory?.name || room?.room_category?.name || room?.room_category_name || ""
      ).trim();
      if (id == null || !name) return;
      if (!map[String(id)]) map[String(id)] = name;
    });

    return map;
  }, [roomCategories, rooms]);

  const summary = useMemo(() => {
    const now = isDemoMode() ? demoReferenceNow() : new Date();
    const today = toIsoDateLocal(now);
    const monthStart = startOfMonthIso(now);
    const monthEnd = endOfMonthIso(now);
    const yearStart = `${now.getFullYear()}-01-01`;

    const allReservations = Array.isArray(reservations) ? reservations : [];
    const active = allReservations.filter((r) => isOperational(r?.status));

    let arrivals = 0;
    let arrivalsActual = 0;
    let departures = 0;
    let departuresActual = 0;
    let stayovers = 0;
    let inHouse = 0;

    let pickupGross = 0;
    let pickupGrossNights = 0;
    let pickupGrossRevenue = 0;

    let bookingsToday = 0;
    let bookingsMtd = 0;
    let bookingsYtd = 0;

    let stayNightsToday = 0;
    let stayNightsMtd = 0;
    let stayNightsYtd = 0;

    let stayRevenueToday = 0;
    let stayRevenueMtd = 0;
    let stayRevenueYtd = 0;

    let adultsToday = 0;
    let adultsMtd = 0;
    let adultsYtd = 0;

    let childrenToday = 0;
    let childrenMtd = 0;
    let childrenYtd = 0;

    let nightsMtdForLos = 0;
    let createdTodayCount = 0;
    let createdMtdCount = 0;
    let createdYtdCount = 0;
    let cancelledToday = 0;
    let cancelledMtd = 0;
    let cancelledYtd = 0;
    let noShowsMtd = 0;

    const byClient = new Map();
    const byCategory = new Map();

    active.forEach((r) => {
      const checkIn = normalizeDate(r?.check_in_date);
      const checkOut = normalizeDate(r?.check_out_date);
      const created = normalizeDate(r?.createdAt || r?.created_at);
      const arrivalDate = normalizeDate(r?.arrival_date);
      const departureDate = normalizeDate(r?.departure_date);

      if (checkIn === today) arrivals += 1;
      if (checkOut === today) departures += 1;
      if (
        arrivalDate === today ||
        (!arrivalDate && String(r?.status || "").toLowerCase() === "checked in" && checkIn === today)
      ) {
        arrivalsActual += 1;
      }
      if (
        departureDate === today ||
        (!departureDate && String(r?.status || "").toLowerCase() === "checked out" && checkOut === today)
      ) {
        departuresActual += 1;
      }

      const nights = reservationNights(r);
      const revenue = toRevenue(r);
      const nightly = nights > 0 ? revenue / nights : 0;
      const adults = Math.max(0, toNum(r?.adults));
      const children = Math.max(0, toNum(r?.children));

      const oToday = overlapNights(checkIn, checkOut, today, today);
      const oMtd = overlapNights(checkIn, checkOut, monthStart, today);
      const oYtd = overlapNights(checkIn, checkOut, yearStart, today);

      stayNightsToday += oToday;
      stayNightsMtd += oMtd;
      stayNightsYtd += oYtd;

      stayRevenueToday += oToday * nightly;
      stayRevenueMtd += oMtd * nightly;
      stayRevenueYtd += oYtd * nightly;

      adultsToday += adults * oToday;
      adultsMtd += adults * oMtd;
      adultsYtd += adults * oYtd;

      childrenToday += children * oToday;
      childrenMtd += children * oMtd;
      childrenYtd += children * oYtd;

      if (created === today) {
        pickupGross += 1;
        pickupGrossNights += nights;
        pickupGrossRevenue += revenue;
        bookingsToday += 1;
      }

      if (checkIn <= today && checkOut > today) {
        inHouse += 1;
      }

      if (checkIn < today && checkOut > today) {
        stayovers += 1;
      }

      if (created >= monthStart && created <= today) {
        bookingsMtd += 1;
        nightsMtdForLos += nights;
        const rateClientId = r?.rate?.client_id ?? r?.client_id ?? r?.client?.id ?? null;
        const clientLabel =
          (rateClientId != null ? clientMap[String(rateClientId)] : null) ||
          String(
            r?.external_agency_name || r?.rate?.client?.name || r?.client?.name || dashboardCopy.fallback.directReservation
          ).trim();

        const categoryId = r?.room_category_id ?? r?.room_category?.id ?? r?.roomCategory?.id ?? null;
        const categoryLabel =
          (categoryId != null ? categoryMap[String(categoryId)] : null) ||
          String(
            r?.room_category?.name || r?.room_category_name || dashboardCopy.fallback.category(categoryId || "?")
          ).trim();

        const c = byClient.get(clientLabel) || { key: clientLabel, revenue: 0, bookings: 0 };
        c.revenue += revenue;
        c.bookings += 1;
        byClient.set(clientLabel, c);

        const k = byCategory.get(categoryLabel) || { key: categoryLabel, revenue: 0, bookings: 0 };
        k.revenue += revenue;
        k.bookings += 1;
        byCategory.set(categoryLabel, k);
      }

      if (created >= yearStart && created <= today) bookingsYtd += 1;
    });

    allReservations.forEach((reservation) => {
      const created = normalizeDate(reservation?.createdAt || reservation?.created_at);
      const cancelledOn = cancellationDate(reservation);

      if (created === today) createdTodayCount += 1;
      if (created >= monthStart && created <= today) {
        createdMtdCount += 1;
        if (isNoShow(reservation?.status)) noShowsMtd += 1;
      }
      if (created >= yearStart && created <= today) createdYtdCount += 1;

      if (!isCancelled(reservation?.status)) return;
      if (cancelledOn === today) cancelledToday += 1;
      if (cancelledOn >= monthStart && cancelledOn <= today) cancelledMtd += 1;
      if (cancelledOn >= yearStart && cancelledOn <= today) cancelledYtd += 1;
    });

    const totalRooms = Array.isArray(rooms) ? rooms.length : 0;
    const occToday = totalRooms > 0 ? (stayNightsToday / totalRooms) * 100 : 0;
    const occMtd = totalRooms > 0 ? (stayNightsMtd / (totalRooms * now.getDate())) * 100 : 0;
    const occYtd = totalRooms > 0 ? (stayNightsYtd / (totalRooms * yearDayIndex(now))) * 100 : 0;

    const adrToday = stayNightsToday > 0 ? stayRevenueToday / stayNightsToday : 0;
    const adrMtd = stayNightsMtd > 0 ? stayRevenueMtd / stayNightsMtd : 0;
    const adrYtd = stayNightsYtd > 0 ? stayRevenueYtd / stayNightsYtd : 0;

    const revParToday = totalRooms > 0 ? stayRevenueToday / totalRooms : 0;
    const revParMtd = totalRooms > 0 ? stayRevenueMtd / (totalRooms * now.getDate()) : 0;
    const revParYtd = totalRooms > 0 ? stayRevenueYtd / (totalRooms * yearDayIndex(now)) : 0;

    const cancellationRateToday = createdTodayCount > 0 ? (cancelledToday / createdTodayCount) * 100 : 0;
    const cancellationRateMtd = createdMtdCount > 0 ? (cancelledMtd / createdMtdCount) * 100 : 0;
    const cancellationRateYtd = createdYtdCount > 0 ? (cancelledYtd / createdYtdCount) * 100 : 0;
    const noShowRateMtd = createdMtdCount > 0 ? (noShowsMtd / createdMtdCount) * 100 : 0;
    const losMtd = bookingsMtd > 0 ? nightsMtdForLos / bookingsMtd : 0;

    const toDist = (map) => {
      const sorted = Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
      const list =
        sorted.length > 8
          ? [
              ...sorted.slice(0, 7),
              sorted.slice(7).reduce(
                (acc, row) => ({
                  key: dashboardCopy.fallback.other(sorted.length - 7),
                  revenue: acc.revenue + row.revenue,
                  bookings: acc.bookings + row.bookings,
                }),
                { key: dashboardCopy.fallback.otherBase, revenue: 0, bookings: 0 }
              ),
            ]
          : sorted;
      const total = list.reduce((sum, row) => sum + row.revenue, 0);
        return list.map((row, idx) => ({
          ...row,
          share: total > 0 ? (row.revenue / total) * 100 : 0,
          color: donutPalette[idx % donutPalette.length],
        }));
      };

    const monthDays = daysInMonth(now);
    const monthOccSeries = Array.from({ length: monthDays }).map((_, dayIndex) => {
      const date = new Date(now.getFullYear(), now.getMonth(), dayIndex + 1);
      return {
        dateIso: toIsoDateLocal(date),
        dayLabel: date.toLocaleDateString(textLocale, { day: "2-digit", month: "short" }),
        dayTick: date.toLocaleDateString(textLocale, { day: "2-digit" }),
      };
    });
    const monthOccIndex = monthOccSeries.reduce((acc, row, idx) => {
      acc[row.dateIso] = idx;
      return acc;
    }, {});

    const chartCategoryMeta = new Map();
    const ensureChartCategory = (categoryId, categoryName) => {
      const rawId = categoryId != null ? String(categoryId) : "unknown";
      const key = `cat_${rawId.replace(/[^a-zA-Z0-9_]/g, "_")}`;
      if (!chartCategoryMeta.has(key)) {
        chartCategoryMeta.set(key, {
          key,
          label:
            categoryName ||
            (rawId === "unknown" ? dashboardCopy.fallback.unmappedCategory : dashboardCopy.fallback.category(rawId)),
          capacity: 0,
          used: 0,
        });
      }
      return key;
    };

    (Array.isArray(rooms) ? rooms : []).forEach((room) => {
      const categoryId = room?.room_category_id ?? room?.room_category?.id ?? room?.RoomCategory?.id ?? null;
      const categoryName = String(
        (categoryId != null ? categoryMap[String(categoryId)] : "") ||
          room?.RoomCategory?.name ||
          room?.room_category?.name ||
          room?.room_category_name ||
          ""
      ).trim();
      const key = ensureChartCategory(categoryId, categoryName);
      const meta = chartCategoryMeta.get(key);
      if (meta) meta.capacity += 1;
    });

    const monthEndExclusive = addDays(monthEnd, 1);
    active.forEach((reservation) => {
      const checkIn = normalizeDate(reservation?.check_in_date);
      const checkOut = normalizeDate(reservation?.check_out_date);

      const categoryId =
        reservation?.room_category_id ??
        reservation?.room_category?.id ??
        reservation?.roomCategory?.id ??
        reservation?.room?.room_category_id ??
        null;
      const categoryName = String(
        (categoryId != null ? categoryMap[String(categoryId)] : "") ||
          reservation?.room_category?.name ||
          reservation?.room_category_name ||
          reservation?.room?.RoomCategory?.name ||
          reservation?.room?.room_category?.name ||
          (categoryId != null ? dashboardCopy.fallback.category(categoryId) : dashboardCopy.fallback.unmappedCategory)
      ).trim();
      const categoryKey = ensureChartCategory(categoryId, categoryName);

      const overlapStart = checkIn > monthStart ? checkIn : monthStart;
      const overlapEnd = checkOut < monthEndExclusive ? checkOut : monthEndExclusive;
      const startDate = parseDateOnly(overlapStart);
      const endDate = parseDateOnly(overlapEnd);
      if (!startDate || !endDate || endDate <= startDate) return;

      for (let cursor = new Date(startDate.getTime()); cursor < endDate; cursor.setDate(cursor.getDate() + 1)) {
        const iso = toIsoDateLocal(cursor);
        const idx = monthOccIndex[iso];
        if (idx == null) continue;
        const row = monthOccSeries[idx];
        row[categoryKey] = toNum(row[categoryKey]) + 1;
        const meta = chartCategoryMeta.get(categoryKey);
        if (meta) meta.used += 1;
      }
    });

    const rankedChartCategories = Array.from(chartCategoryMeta.values())
      .filter((meta) => meta.capacity > 0 || meta.used > 0)
      .sort((a, b) => b.capacity - a.capacity || b.used - a.used || a.label.localeCompare(b.label, sortLocale));

    const mainChartCategories = rankedChartCategories.slice(0, 6);
    const otherChartCategories = rankedChartCategories.slice(6);

    if (otherChartCategories.length > 0) {
      const otherKey = "cat_other";
      monthOccSeries.forEach((row) => {
        row[otherKey] = otherChartCategories.reduce((sum, category) => sum + toNum(row[category.key]), 0);
      });
      mainChartCategories.push({
        key: otherKey,
        label: dashboardCopy.fallback.other(otherChartCategories.length),
        capacity: otherChartCategories.reduce((sum, category) => sum + toNum(category.capacity), 0),
        used: otherChartCategories.reduce((sum, category) => sum + toNum(category.used), 0),
      });
    }

    monthOccSeries.forEach((row) => {
      mainChartCategories.forEach((category) => {
        row[category.key] = toNum(row[category.key]);
      });

      const occupiedRooms = mainChartCategories.reduce((sum, category) => sum + toNum(row[category.key]), 0);
      const occPct = totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0;
      row.occupiedRooms = occupiedRooms;
      row.occPct = occPct;
      row.totalOccReal = row.dateIso <= today ? occupiedRooms : null;
      row.totalOccForecast = row.dateIso > today ? occupiedRooms : null;
    });

    const monthForecast = Array.from({ length: FORECAST_MONTH_COUNT }).map((_, idx) => {
      const monthDate = new Date(now.getFullYear(), now.getMonth() + idx, 1);
      const fStart = startOfMonthIso(monthDate);
      const fEnd = endOfMonthIso(monthDate);
      const monthCapacity = totalRooms * daysInMonth(monthDate);

      const pickups = PICKUP_WINDOWS.reduce((acc, window) => {
        acc[window.key] = {
          label: window.label,
          netNights: 0,
          occDeltaPct: 0,
        };
        return acc;
      }, {});

      let otbNights = 0;
      let otbRevenue = 0;

      allReservations.forEach((reservation) => {
        const checkIn = normalizeDate(reservation?.check_in_date);
        const checkOut = normalizeDate(reservation?.check_out_date);
        const overlap = overlapNights(checkIn, checkOut, fStart, fEnd);
        if (overlap <= 0) return;

        const created = normalizeDate(reservation?.createdAt || reservation?.created_at);
        const updated = cancellationDate(reservation);
        const operational = isOperational(reservation?.status);
        const cancelled = isCancelled(reservation?.status);
        const revenue = toRevenue(reservation);
        const nights = reservationNights(reservation);
        const overlapRevenue = nights > 0 ? (revenue / nights) * overlap : 0;

        if (operational) {
          otbNights += overlap;
          otbRevenue += overlapRevenue;
        }

        PICKUP_WINDOWS.forEach((window) => {
          const pickup = pickups[window.key];
          if (operational && isWithinWindow(created, today, window.days)) {
            pickup.netNights += overlap;
          }
          if (cancelled && isWithinWindow(updated, today, window.days)) {
            pickup.netNights -= overlap;
          }
        });
      });

      PICKUP_WINDOWS.forEach((window) => {
        const pickup = pickups[window.key];
        pickup.occDeltaPct = monthCapacity > 0 ? (pickup.netNights / monthCapacity) * 100 : 0;
      });

      return {
        idx,
        isCurrentMonth: idx === 0,
        shortLabel: monthLabel(monthDate, "short", textLocale),
        start: fStart,
        end: fEnd,
        capacity: monthCapacity,
        otbNights,
        otbRevenue,
        forecastOccPct: monthCapacity > 0 ? (otbNights / monthCapacity) * 100 : 0,
        pickups,
      };
    });

    const upcomingEvents = (Array.isArray(events) ? events : [])
      .map((e) => ({ ...e, start: normalizeDate(e?.start_date), end: normalizeDate(e?.end_date || e?.start_date) }))
      .filter((e) => e.start && e.end && e.end >= today)
      .sort((a, b) => String(a.start).localeCompare(String(b.start)))
      .slice(0, 6);

    return {
      now,
      today,
      arrivals,
      arrivalsActual,
      departures,
      departuresActual,
      stayovers,
      inHouse,
      pickupNet: pickupGross - cancelledToday,
      pickupGross,
      pickupGrossNights,
      pickupGrossRevenue,
      cancelledToday,
      cancelledMtd,
      cancelledYtd,
      createdTodayCount,
      createdMtdCount,
      createdYtdCount,
      totalRooms,
      monthStart,
      monthEnd,
      bookingsToday,
      bookingsMtd,
      bookingsYtd,
      stayNightsToday,
      stayNightsMtd,
      stayNightsYtd,
      stayRevenueToday,
      stayRevenueMtd,
      stayRevenueYtd,
      adultsToday,
      adultsMtd,
      adultsYtd,
      childrenToday,
      childrenMtd,
      childrenYtd,
      occToday,
      occMtd,
      occYtd,
      adrToday,
      adrMtd,
      adrYtd,
      revParToday,
      revParMtd,
      revParYtd,
      cancellationRateToday,
      cancellationRateMtd,
      cancellationRateYtd,
      noShowRateMtd,
      losMtd,
      monthOccSeries,
      monthOccCategories: mainChartCategories,
      monthOccAllCategories: rankedChartCategories,
      monthForecast,
      currentMonthForecast: monthForecast[0] || null,
      byClient: toDist(byClient),
      byCategory: toDist(byCategory),
      upcomingEvents,
    };
  }, [reservations, rooms, events, clientMap, categoryMap, dashboardCopy.fallback, donutPalette, sortLocale, textLocale]);

  const formatInt = (value) => intFmt.format(Math.round(Number(value) || 0));
  const formatMoney = (value) => formatFromBase(Number(value) || 0, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
  const formatMoneyPrecise = (value) => formatFromBase(Number(value) || 0);
  const formatPct = (value) => `${(Number(value) || 0).toFixed(1)}%`;
  const formatSignedPct = (value) => {
    const num = toNum(value);
    const sign = num > 0 ? "+" : "";
    return `${sign}${num.toFixed(2)}%`;
  };
  const deltaClass = (value) => {
    const num = toNum(value);
    if (num > 0.0001) return "pd-deltaPos";
    if (num < -0.0001) return "pd-deltaNeg";
    return "pd-deltaFlat";
  };
  const chartTheme = useMemo(
    () =>
      isLightTheme
        ? {
            grid: "rgba(34, 58, 122, 0.1)",
            axis: "rgba(23, 38, 83, 0.76)",
            axisMuted: "rgba(23, 38, 83, 0.62)",
            referenceLine: "rgba(34, 58, 122, 0.3)",
            legend: "rgba(23, 38, 83, 0.76)",
            tooltipBackground: "rgba(255, 255, 255, 0.98)",
            tooltipBorder: "1px solid rgba(34, 58, 122, 0.14)",
            tooltipText: "#172653",
            tooltipSub: "rgba(23, 38, 83, 0.62)",
            lineActual: "#223a7a",
            lineForecast: "#3db6e9",
            pieStroke: "rgba(255, 255, 255, 0.98)",
            pieLabel: "#ffffff",
          }
        : {
            grid: "rgba(61, 182, 233, 0.12)",
            axis: "rgba(255, 255, 255, 0.78)",
            axisMuted: "rgba(255, 255, 255, 0.66)",
            referenceLine: "rgba(99, 193, 199, 0.42)",
            legend: "rgba(255, 255, 255, 0.74)",
            tooltipBackground: "rgba(23, 38, 83, 0.98)",
            tooltipBorder: "1px solid rgba(61, 182, 233, 0.32)",
            tooltipText: "rgba(255, 255, 255, 0.94)",
            tooltipSub: "rgba(255, 255, 255, 0.64)",
            lineActual: "#ffffff",
            lineForecast: "#3db6e9",
            pieStroke: "rgba(23, 38, 83, 0.82)",
            pieLabel: "#ffffff",
          },
    [isLightTheme]
  );
  const renderDonutTooltip = ({ active, payload }) => {
    if (!active || !Array.isArray(payload) || payload.length === 0) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    return (
      <div
        style={{
          background: chartTheme.tooltipBackground,
          border: chartTheme.tooltipBorder,
          borderRadius: 10,
          color: chartTheme.tooltipText,
          padding: "8px 10px",
        }}
      >
        <div style={{ fontSize: "0.74rem", fontWeight: 800 }}>{row.key}</div>
        <div style={{ marginTop: 2, fontSize: "0.72rem" }}>
          {formatMoney(row.revenue)} | {formatPct(row.share)}
        </div>
        <div style={{ marginTop: 2, fontSize: "0.68rem", color: chartTheme.tooltipSub }}>
          {formatInt(row.bookings)} {dashboardCopy.chart.bookings}
        </div>
      </div>
    );
  };
  const renderDonutSliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const share = toNum(percent) * 100;
    if (share < PIE_LABEL_MIN_SHARE) return null;
    const radius = Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) * 0.52;
    const x = Number(cx) + radius * Math.cos(-midAngle * RADIAN);
    const y = Number(cy) + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill={chartTheme.pieLabel}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 12, fontWeight: 900, pointerEvents: "none" }}
      >
        {`${share.toFixed(0)}%`}
      </text>
    );
  };
  const areaColorsByKey = useMemo(() => {
    const map = {};
    (Array.isArray(summary.monthOccCategories) ? summary.monthOccCategories : []).forEach((category, idx) => {
      map[category.key] = areaPalette[idx % areaPalette.length];
    });
    return map;
  }, [areaPalette, summary.monthOccCategories]);

  const selectedOccupancyDay = summary.monthOccSeries.find((row) =>
    occupancySelection?.businessUnitId === businessUnitId && row.dateIso === occupancySelection.dateIso
  ) || summary.monthOccSeries.find((row) => row.dateIso === summary.today) || summary.monthOccSeries[0];
  const selectOccupancyDay = (dateIso) => {
    if (!summary.monthOccSeries.some((row) => row.dateIso === dateIso)) return;
    setOccupancySelection((previous) => previous?.businessUnitId === businessUnitId && previous.dateIso === dateIso
      ? previous : { businessUnitId, dateIso });
  };
  const inspectOccupancyDay = (state) => {
    if (state?.isTooltipActive && state.activeLabel) selectOccupancyDay(state.activeLabel);
  };

  const updatedLabel = updatedAt
    ? new Date(updatedAt).toLocaleString(languageId === "en" ? "es-ES" : textLocale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

  const cards = [
    {
      key: "daily-flow",
      label: dashboardCopy.cards.dailyFlow,
      value: "",
      hint: dashboardCopy.cards.dailyFlowHint(formatInt(summary.inHouse)),
      Icon: FiUsers,
    },
    {
      key: "pickup",
      label: dashboardCopy.cards.pickup,
      value: `${formatInt(summary.currentMonthForecast?.pickups?.m1?.netNights || 0)} ${dashboardCopy.table.roomNightsShort}`,
      hint: dashboardCopy.cards.pickupHint(formatSignedPct(summary.currentMonthForecast?.pickups?.m1?.occDeltaPct || 0)),
      Icon: FiTrendingUp,
    },
    {
      key: "forecast",
      label: dashboardCopy.cards.forecast,
      value: formatPct(summary.currentMonthForecast?.forecastOccPct || 0),
      hint: dashboardCopy.cards.forecastHint(
        formatInt(summary.currentMonthForecast?.otbNights || 0),
        formatInt(summary.currentMonthForecast?.capacity || 0)
      ),
      Icon: FiCalendar,
    },
    {
      key: "revpar",
      label: dashboardCopy.cards.revpar,
      value: formatMoneyPrecise(summary.revParToday),
      hint: dashboardCopy.cards.revparHint(formatMoneyPrecise(summary.adrToday), formatPct(summary.occToday)),
      Icon: FiActivity,
    },
  ];

  return (
    <div className={`pd-page${isLightTheme ? " pd-page--light" : ""}`}>
      <style>{uiCss}</style>
      <div className="pd-shell">
        <div className="pd-header">
          <div>
            <h2 className="pd-title">{dashboardCopy.title}</h2>
            <div className="pd-sub">
              {dashboardCopy.subtitle(monthLabel(summary.now || new Date(), "long", textLocale))}
            </div>
            <div className="pd-dateHero">{formatHeroDate(summary.now || new Date(), languageId, textLocale)}</div>
          </div>
          <div className="pd-actions">
            <span className="pd-pill">{dashboardCopy.updated} {updatedLabel}</span>
            <span className="pd-pill">
              {dashboardCopy.cancelMtd} {formatPct(summary.cancellationRateMtd)} | {dashboardCopy.noShowMtd}{" "}
              {formatPct(summary.noShowRateMtd)}
            </span>
            <span className="pd-pill">{dashboardCopy.losMtd} {toNum(summary.losMtd).toFixed(2)}</span>
            <button type="button" className="pd-btn" onClick={() => setReloadKey((v) => v + 1)}>
              <FiRefreshCw size={14} /> {dashboardCopy.refresh}
            </button>
          </div>
        </div>

        <div className="pd-body">
          {!canViewDashboard ? (
            <div className="pd-state">
              <div style={{ fontWeight: 800 }}>{dashboardCopy.states.noPermission}</div>
            </div>
          ) : loading ? (
            <div className="pd-state">
              <div className="pd-spin" aria-hidden="true" />
              <div>{dashboardCopy.states.loading}</div>
            </div>
          ) : error ? (
            <div className="pd-state">
              <div style={{ fontWeight: 800 }}>{dashboardCopy.states.loadErrorTitle}</div>
              <div style={{ color: isLightTheme ? "#a44a57" : "rgba(255,149,149,0.95)" }}>{error}</div>
            </div>
          ) : (
            <>
              {multiCurrencyEnabled && exchangeRateRows.length > 0 ? (
                <section className="pd-fxStrip" aria-label={dashboardCopy.exchangeRates.title}>
                  <div className="pd-fxHeading">
                    <span className="pd-fxIcon" aria-hidden="true"><FiDollarSign size={16} /></span>
                    <div>
                      <div className="pd-fxTitle">{dashboardCopy.exchangeRates.title}</div>
                      <div className="pd-fxSubtitle">{dashboardCopy.exchangeRates.subtitle}</div>
                    </div>
                  </div>
                  <div className="pd-fxRates">
                    <span className="pd-fxChip pd-fxChip--base">
                      {dashboardCopy.exchangeRates.base}: {baseCurrency}
                    </span>
                    {exchangeRateRows.map((row) => (
                      <span
                        key={row.code}
                        className={`pd-fxChip${row.code === displayCurrency ? " pd-fxChip--selected" : ""}`}
                      >
                        1 {row.code} = <strong>{formatExchangeRate(row.rate, textLocale)}</strong> {baseCurrency}
                      </span>
                    ))}
                  </div>
                  {currencyConfiguration?.effective_from ? (
                    <div className="pd-fxEffective">
                      {dashboardCopy.exchangeRates.effective}: {new Date(currencyConfiguration.effective_from).toLocaleString(textLocale)}
                    </div>
                  ) : null}
                </section>
              ) : null}

              <div className="pd-cards">
                {cards.map((card) => {
                  const Icon = card.Icon;
                  return (
                    <div key={card.key} className="pd-card">
                      <div className="pd-cardHead">
                        <div className="pd-cardLabel">{card.label}</div>
                        <Icon size={16} color={isLightTheme ? "#5d7684" : "rgba(230,230,247,0.88)"} />
                      </div>
                      {card.key === "daily-flow" ? (
                        <>
                          <div className="pd-flowGrid">
                            <div className="pd-flowItem">
                              <div className="pd-flowLabel">{dashboardCopy.flow.arrivals}</div>
                              <div className="pd-flowMain">{formatInt(summary.arrivals)}</div>
                              <div className="pd-flowSub">{dashboardCopy.flow.checkedIn(formatInt(summary.arrivalsActual))}</div>
                            </div>
                            <div className="pd-flowItem">
                              <div className="pd-flowLabel">{dashboardCopy.flow.departures}</div>
                              <div className="pd-flowMain">{formatInt(summary.departures)}</div>
                              <div className="pd-flowSub">{dashboardCopy.flow.checkedOut(formatInt(summary.departuresActual))}</div>
                            </div>
                            <div className="pd-flowItem">
                              <div className="pd-flowLabel">{dashboardCopy.flow.stayovers}</div>
                              <div className="pd-flowMain">{formatInt(summary.stayovers)}</div>
                              <div className="pd-flowSub">{dashboardCopy.flow.inHouse(formatInt(summary.inHouse))}</div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="pd-cardValue">{card.value}</div>
                          <div className="pd-cardHint">{card.hint}</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="pd-grid">
                <section className="pd-panel pd-events">
                  <div className="pd-panelHeader">
                    <h3 className="pd-panelTitle">
                      <FiBarChart2 size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} />
                      {dashboardCopy.panels.monthlyCurve(monthLabel(summary.now || new Date(), "long", textLocale))}
                    </h3>
                    <span className="pd-panelSub">{dashboardCopy.panels.monthlyCurveSubtitle}</span>
                  </div>
                  <div className="pd-occupancyLayout">
                  <div className="pd-chartWrap">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={summary.monthOccSeries}
                        margin={{ top: 6, right: 18, left: 6, bottom: 2 }}
                        onMouseMove={inspectOccupancyDay}
                        onMouseEnter={inspectOccupancyDay}
                        onClick={inspectOccupancyDay}
                        accessibilityLayer
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
                        <XAxis
                          dataKey="dateIso"
                          minTickGap={14}
                          tick={{ fill: chartTheme.axis, fontSize: 11 }}
                          tickFormatter={(iso) => String(iso).slice(8, 10)}
                        />
                        <YAxis
                          yAxisId="rooms"
                          tick={{ fill: chartTheme.axisMuted, fontSize: 11 }}
                          width={34}
                        />
                        <Tooltip content={() => null} cursor={{ stroke: chartTheme.referenceLine, strokeWidth: 1 }} />
                        <Legend wrapperStyle={{ fontSize: "11px", color: chartTheme.legend }} />
                        <ReferenceLine
                          x={summary.today}
                          stroke={chartTheme.referenceLine}
                          strokeDasharray="4 3"
                          yAxisId="rooms"
                          ifOverflow="extendDomain"
                        />
                        {summary.monthOccCategories.map((category) => (
                          <Area
                            key={category.key}
                            yAxisId="rooms"
                            type="monotone"
                            dataKey={category.key}
                            name={category.label}
                            stackId="occ"
                            stroke={areaColorsByKey[category.key] || "#3db6e9"}
                            fill={areaColorsByKey[category.key] || "#3db6e9"}
                            fillOpacity={0.58}
                            isAnimationActive={false}
                          />
                        ))}
                        <Line
                          yAxisId="rooms"
                          type="monotone"
                          dataKey="totalOccReal"
                          name={dashboardCopy.chart.totalOccupancy}
                          stroke={chartTheme.lineActual}
                          strokeWidth={2.6}
                          dot={false}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                        <Line
                          yAxisId="rooms"
                          type="monotone"
                          dataKey="totalOccForecast"
                          name={dashboardCopy.chart.totalOccupancyForecast}
                          stroke={chartTheme.lineForecast}
                          strokeWidth={2.2}
                          strokeDasharray="6 4"
                          dot={false}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <PmsOccupancyDetails
                    day={selectedOccupancyDay}
                    days={summary.monthOccSeries}
                    categories={summary.monthOccAllCategories}
                    totalRooms={summary.totalRooms}
                    colors={areaColorsByKey}
                    languageId={languageId}
                    locale={textLocale}
                    onDayChange={selectOccupancyDay}
                  />
                  </div>
                  <div className="pd-chartLegend">
                    {dashboardCopy.panels.monthlyCurveLegend(summary.today)}
                  </div>
                </section>

                <section className="pd-panel pd-panel--table">
                  <div className="pd-panelHeader">
                    <h3 className="pd-panelTitle">
                      <FiBarChart2 size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} />
                      {dashboardCopy.panels.kpiMatrix(monthLabel(summary.now || new Date(), "long", textLocale))}
                    </h3>
                    <span className="pd-panelSub">{dashboardCopy.panels.kpiMatrixSubtitle}</span>
                  </div>
                  <div className="pd-tableWrap">
                    <table className="pd-table">
                      <thead>
                        <tr>
                          <th>{dashboardCopy.table.metric}</th>
                          <th>{dashboardCopy.table.today}</th>
                          <th>{dashboardCopy.table.mtd}</th>
                          <th>{dashboardCopy.table.ytd}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{dashboardCopy.table.bookingsCreated}</td>
                          <td>{formatInt(summary.createdTodayCount)}</td>
                          <td>{formatInt(summary.createdMtdCount)}</td>
                          <td>{formatInt(summary.createdYtdCount)}</td>
                        </tr>
                        <tr>
                          <td>{dashboardCopy.table.cancellations}</td>
                          <td>
                            <span className="pd-tableMetric">
                              <span className="pd-kicker">{formatInt(summary.cancelledToday)}</span>
                              <span className="pd-tableMetricSub">{formatPct(summary.cancellationRateToday)}</span>
                            </span>
                          </td>
                          <td>
                            <span className="pd-tableMetric">
                              <span className="pd-kicker">{formatInt(summary.cancelledMtd)}</span>
                              <span className="pd-tableMetricSub">{formatPct(summary.cancellationRateMtd)}</span>
                            </span>
                          </td>
                          <td>
                            <span className="pd-tableMetric">
                              <span className="pd-kicker">{formatInt(summary.cancelledYtd)}</span>
                              <span className="pd-tableMetricSub">{formatPct(summary.cancellationRateYtd)}</span>
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>{dashboardCopy.table.roomNightsSold}</td>
                          <td>{formatInt(summary.stayNightsToday)}</td>
                          <td>{formatInt(summary.stayNightsMtd)}</td>
                          <td>{formatInt(summary.stayNightsYtd)}</td>
                        </tr>
                        <tr>
                          <td>{dashboardCopy.table.revenue}</td>
                          <td>{formatMoney(summary.stayRevenueToday)}</td>
                          <td>{formatMoney(summary.stayRevenueMtd)}</td>
                          <td>{formatMoney(summary.stayRevenueYtd)}</td>
                        </tr>
                        <tr>
                          <td>{dashboardCopy.table.adr}</td>
                          <td>{formatMoneyPrecise(summary.adrToday)}</td>
                          <td>{formatMoneyPrecise(summary.adrMtd)}</td>
                          <td>{formatMoneyPrecise(summary.adrYtd)}</td>
                        </tr>
                        <tr>
                          <td>{dashboardCopy.table.revpar}</td>
                          <td>{formatMoneyPrecise(summary.revParToday)}</td>
                          <td>{formatMoneyPrecise(summary.revParMtd)}</td>
                          <td>{formatMoneyPrecise(summary.revParYtd)}</td>
                        </tr>
                        <tr>
                          <td>{dashboardCopy.table.occupancy}</td>
                          <td>{formatPct(summary.occToday)}</td>
                          <td>{formatPct(summary.occMtd)}</td>
                          <td>{formatPct(summary.occYtd)}</td>
                        </tr>
                        <tr>
                          <td>{dashboardCopy.table.adults}</td>
                          <td>{formatInt(summary.adultsToday)}</td>
                          <td>{formatInt(summary.adultsMtd)}</td>
                          <td>{formatInt(summary.adultsYtd)}</td>
                        </tr>
                        <tr>
                          <td>{dashboardCopy.table.children}</td>
                          <td>{formatInt(summary.childrenToday)}</td>
                          <td>{formatInt(summary.childrenMtd)}</td>
                          <td>{formatInt(summary.childrenYtd)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="pd-matrixNote">{dashboardCopy.table.note}</div>
                </section>

                <section className="pd-panel">
                  <div className="pd-panelHeader">
                    <h3 className="pd-panelTitle">
                      <FiUsers size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} />
                      {dashboardCopy.panels.salesByClient}
                    </h3>
                  </div>
                  <div className="pd-donutPanel">
                    {summary.byClient.length === 0 ? (
                      <div className="pd-donutEmpty">{dashboardCopy.empty.noDataMonth}</div>
                    ) : (
                      <>
                        <div className="pd-donutChart">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Tooltip content={renderDonutTooltip} />
                              <Pie
                                data={summary.byClient}
                                dataKey="revenue"
                                nameKey="key"
                                innerRadius="56%"
                                outerRadius="82%"
                                paddingAngle={2}
                                stroke={chartTheme.pieStroke}
                                strokeWidth={2}
                                label={renderDonutSliceLabel}
                                labelLine={false}
                                isAnimationActive={false}
                              >
                                {summary.byClient.map((row, idx) => (
                                  <Cell key={`client-slice-${row.key}`} fill={row.color || donutPalette[idx % donutPalette.length]} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="pd-donutLegend">
                          {summary.byClient.map((row, idx) => (
                            <div key={`client-${row.key}`} className="pd-donutItem">
                              <span
                                className="pd-donutSwatch"
                                style={{ background: row.color || donutPalette[idx % donutPalette.length] }}
                              />
                              <div className="pd-donutLabel">
                                <div className="pd-donutName" title={row.key}>{row.key}</div>
                                <div className="pd-donutMeta">{formatMoney(row.revenue)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </section>

                <section className="pd-panel">
                  <div className="pd-panelHeader">
                    <h3 className="pd-panelTitle">
                      <FiCalendar size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} />
                      {dashboardCopy.panels.salesByCategory}
                    </h3>
                  </div>
                  <div className="pd-donutPanel">
                    {summary.byCategory.length === 0 ? (
                      <div className="pd-donutEmpty">{dashboardCopy.empty.noDataMonth}</div>
                    ) : (
                      <>
                        <div className="pd-donutChart">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Tooltip content={renderDonutTooltip} />
                              <Pie
                                data={summary.byCategory}
                                dataKey="revenue"
                                nameKey="key"
                                innerRadius="56%"
                                outerRadius="82%"
                                paddingAngle={2}
                                stroke={chartTheme.pieStroke}
                                strokeWidth={2}
                                label={renderDonutSliceLabel}
                                labelLine={false}
                                isAnimationActive={false}
                              >
                                {summary.byCategory.map((row, idx) => (
                                  <Cell key={`category-slice-${row.key}`} fill={row.color || donutPalette[idx % donutPalette.length]} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="pd-donutLegend">
                          {summary.byCategory.map((row, idx) => (
                            <div key={`category-${row.key}`} className="pd-donutItem">
                              <span
                                className="pd-donutSwatch"
                                style={{ background: row.color || donutPalette[idx % donutPalette.length] }}
                              />
                              <div className="pd-donutLabel">
                                <div className="pd-donutName" title={row.key}>{row.key}</div>
                                <div className="pd-donutMeta">{formatMoney(row.revenue)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </section>

                <section className="pd-panel pd-panel--table pd-events">
                  <div className="pd-panelHeader">
                    <h3 className="pd-panelTitle">
                      <FiTrendingUp size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} />
                      {dashboardCopy.panels.forecast}
                    </h3>
                    <span className="pd-panelSub">{dashboardCopy.panels.forecastSubtitle}</span>
                  </div>
                  <div className="pd-tableWrap">
                    <table className="pd-table pd-forecastTable">
                      <thead>
                        <tr>
                          <th>{dashboardCopy.table.month}</th>
                          <th>{dashboardCopy.table.forecastOcc}</th>
                          <th>{dashboardCopy.table.otbNights}</th>
                          <th>{dashboardCopy.table.otbRevenue}</th>
                          <th>{dashboardCopy.table.pickup1m}</th>
                          <th>{dashboardCopy.table.pickup1w}</th>
                          <th>{dashboardCopy.table.pickup1d}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.monthForecast.map((month) => {
                          const p1m = month.pickups.m1;
                          const p1w = month.pickups.w1;
                          const p1d = month.pickups.d1;
                          return (
                            <tr key={month.start}>
                              <td>
                                <span className="pd-forecastMonth">
                                  {month.shortLabel}
                                  {month.isCurrentMonth ? <span className="pd-currentMonthBadge">{dashboardCopy.table.current}</span> : null}
                                </span>
                              </td>
                              <td className="pd-kicker">{formatPct(month.forecastOccPct)}</td>
                              <td>{formatInt(month.otbNights)}</td>
                              <td>{formatMoney(month.otbRevenue)}</td>
                              <td>
                                <span className="pd-kicker">{formatInt(p1m.netNights)} {dashboardCopy.table.roomNightsShort}</span>
                                <span className={` ${deltaClass(p1m.occDeltaPct)}`}> ({formatSignedPct(p1m.occDeltaPct)})</span>
                              </td>
                              <td>
                                <span className="pd-kicker">{formatInt(p1w.netNights)} {dashboardCopy.table.roomNightsShort}</span>
                                <span className={` ${deltaClass(p1w.occDeltaPct)}`}> ({formatSignedPct(p1w.occDeltaPct)})</span>
                              </td>
                              <td>
                                <span className="pd-kicker">{formatInt(p1d.netNights)} {dashboardCopy.table.roomNightsShort}</span>
                                <span className={` ${deltaClass(p1d.occDeltaPct)}`}> ({formatSignedPct(p1d.occDeltaPct)})</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="pd-panel pd-events">
                  <div className="pd-panelHeader">
                    <h3 className="pd-panelTitle">
                      <FiTrendingUp size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} />
                      {dashboardCopy.panels.cityEvents}
                    </h3>
                    <span className="pd-panelSub">{dashboardCopy.panels.cityEventsSubtitle}</span>
                  </div>
                  <div className="pd-list">
                    {summary.upcomingEvents.length === 0 ? (
                      <div className="pd-donutEmpty">
                        {dashboardCopy.events.empty}
                      </div>
                    ) : (
                      summary.upcomingEvents.map((event, idx) => (
                        <div className="pd-event" key={`${event?.name || "event"}-${event?.start || ""}-${idx}`}>
                          <div>
                            <div className="pd-eventName">{event?.name || dashboardCopy.events.special}</div>
                            <div className="pd-eventMeta">
                              {event?.start || "-"} {dashboardCopy.events.to} {event?.end || "-"} | {dashboardCopy.events.attendance}:{" "}
                              {formatInt(event?.expected_attendance || 0)}
                            </div>
                          </div>
                          <span className={`pd-impact ${impactClass(event?.impact_level)}`}>
                            {formatImpactLabel(event?.impact_level, dashboardCopy)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PmsDashboard;
